import Cached from '@mui/icons-material/Cached'
import Check from '@mui/icons-material/Check'
import Info from '@mui/icons-material/Info'
import Person from '@mui/icons-material/Person'
import { Box, Button, Dialog, useMediaQuery, useTheme, TextField, MenuItem, Typography } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react'
import TipTapEditor from './components/TipTapEditor'
import { useNavigate } from 'react-router-dom'
import generatePDF, { Margin } from 'react-to-pdf'
import { toast } from 'react-toastify'
import { FLOW_STATUSES_FOR_LEAVING_SESSION } from 'src/consumer/components/LiveSessionPage/utils/sessionNavigationUtils'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { usePromiseDialog } from 'src/customHooks/usePromiseDialog'
import { validateNextVisitBeforeLeaving, VALIDATION_RESULT } from './utils/nextVisitValidation'
import {
  GENDER_LIST_ONBOARDING,
  THERAPY_DISCIPLINE_NUTRITION_CONSULTATION,
  resolveTherapyDiscipline,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { useCheckedInOrPeekedInChamberRoomId } from 'src/customHooks/customHooks'
import { useResilientAutoSave } from 'src/customHooks/useResilientAutoSave'
import PatientVisitSummaryHandler from 'src/mentor/components/commonReportComponents/components/consultationSummary/PatientVisitSummaryHandler'
import EMRNextVisitComponentModernized from 'src/mentor/components/commonReportComponents/components/nextVisit/prescriptionMakingPageComponents/EMRNextVisitComponentModernized'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import { buildFreeFormRichTextEmrSection } from 'src/mentor/components/EmrRelatedComponents/FreeFormRichText/freeFormRichTextUtils'
// ClinicalDiagnosisEditor and ClinicalDiagnosisEditButton no longer needed - using inline forms
import DayjsDatePicker from 'src/mentor/components/commonReportComponents/components/datePicker/DayjsDatePicker'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  getMPData,
  getMPDataWithoutParams,
  getMPDataWithParams,
  postMPDataWithoutParams,
} from 'src/mentor/services'
import {
  PatientNotesSectionForReports,
  SuggestedTherapySelectMenu,
  getSelectedTherapiesArrayForSubmission,
  getInitialDataForSettingExistingSelectedTherapies,
} from '../PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { commonStyles } from './TherapyReportMakingPageComponents'
import {
  getInitialDataForSettingExistingSelectedTherapiesForTherapyReport,
  getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis,
  getClinicalDiagnosisForSubmission,
} from './TherapyReportMakingPageComponents'
import {
  PMPButtonsContainer,
  PMPSectionHeading,
  PMPSectionInputLabel,
  PrescriptionTabFinalParent,
  SectionSecondaryHeadingAndContent,
} from '../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import ModernSectionHeading from './components/ModernSectionHeading'
import LockIcon from '@mui/icons-material/Lock'
import NotesIcon from '@mui/icons-material/Notes'
import PsychologyIcon from '@mui/icons-material/Psychology'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HealingIcon from '@mui/icons-material/Healing'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import DescriptionIcon from '@mui/icons-material/Description'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import TherapyReportConfirmation from './components/TherapyReportConfirmation'
import TherapyReportSettings from './components/TherapyReportSettings'
import SectionWrapper from './components/SectionWrapper'
import DocumentsUploadSection from './components/DocumentsUploadSection'
import NutritionConsultationSections from './components/NutritionConsultationSections'
import {
  getTherapyReportLabel,
  getTherapyReportSlug,
} from './therapyReportLabels'
import {
  DiagnosisSelectMenu,
  ClinicalDiagnosisSelectMenu,
  getDateOfTherapyReport,
  getDiagnosisArrayForSubmission,
  getClinicalDiagnosisArrayForSubmission,
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingSelectedDiagnosis,
  getInitialDataForSettingExistingSelectedClinicalDiagnosis,
  getNextVisitDateForSubmission,
  PrescriptionTextAreaComponent,
} from './TherapyReportMakingPageComponents'
import {
  PresentingComplaintsSection,
  getComplaintsArrayForSubmission,
  getInitialDataForSettingExistingSelectedComplaints,
} from '../commonReportComponents/PresentingComplaintsSection'
const TherapyReportMakingPage = forwardRef(
  (
    {
      propEventId,
      propPatientId,
      propClinicId,
      propClinicBrandId,
      propMentorProfileData,
      propBookedEventInfo,
      triggerSave,
      setTriggerSave,
      leaveRoom,
      afterSaveFunction,
      auxiliaryDataFromProps,
      getProcessAndSetEventData,
      hideDocSide = false,
    },
    ref,
  ) => {
    const hasMounted = useRef(false)
    const navigate = useNavigate()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [mentorProfileData, setMentorProfileData] = useState()
    const [mentorProfileDataLoading, setMentorProfileDataLoading] = useState(false)
    const [therapyReportId, setTherapyReportId] = useState(null)
    const targetRef = useRef()
    const previewPrintRef = useRef(null) // Ref to access print function from preview
    const urlParams = new URLSearchParams(window.location.search)
    const eventId = useMemo(() => {
      return propEventId || urlParams.get('eventId')
    }, [propEventId, urlParams])
    const patientId = useMemo(() => {
      return propPatientId || urlParams.get('patientId')
    }, [propPatientId, urlParams])
    const clinicId = useMemo(() => {
      return propClinicId || urlParams.get('clinicId')
    }, [propClinicId, urlParams])
    const clinicBrandId = useMemo(() => {
      return propClinicBrandId
    }, [propClinicBrandId])
    const [prescriptionSaveLoading, setPrescriptionSaveLoading] = useState(false)
    const bookedEventInfo = useMemo(() => {
      return propBookedEventInfo
    }, [propBookedEventInfo])

    // Discipline of this therapy session — psychotherapy by default. Nutrition discipline reroutes
    // the editor to render the nutrition EMR section set instead of psychotherapy-specific UI.
    const therapyDiscipline = useMemo(
      () => resolveTherapyDiscipline(propBookedEventInfo),
      [propBookedEventInfo],
    )
    const isNutritionDiscipline = therapyDiscipline === THERAPY_DISCIPLINE_NUTRITION_CONSULTATION
    // EMR sections seeded for therapy-report report type, filtered server-side by discipline so a
    // nutritionist sees only nutrition sections and a psychotherapist sees only psychotherapy
    // (and undeclared/legacy) sections. Falls back to the static nutritionConsultationSectionConfig
    // when the API hasn't been seeded yet, so the editor stays usable.
    const [therapyReportEmrSections, setTherapyReportEmrSections] = useState([])
    useEffect(() => {
      if (!isNutritionDiscipline) return undefined
      let cancelled = false
      const run = async () => {
        try {
          const apiUrl = `/api/secure/practitioner/clinical/emr-settings/emr-sections?includeAll=true&discipline=${encodeURIComponent(
            therapyDiscipline,
          )}`
          const resp = await getMPDataWithoutParams(apiUrl)
          if (cancelled) return
          const dataMap = resp?.data?.data ?? resp?.data ?? {}
          const reportData = dataMap['therapy-report']
          const rawSections = Array.isArray(reportData?.emrSections) ? reportData.emrSections : []
          setTherapyReportEmrSections(rawSections)
        } catch {
          if (!cancelled) setTherapyReportEmrSections([])
        }
      }
      run()
      return () => {
        cancelled = true
      }
    }, [isNutritionDiscipline, therapyDiscipline])

    const [therapyReportData, setTherapyReportData] = useState(null)
    const [observationAndAdvice, setObservationAndAdvice] = useState()
    const [notes, setNotes] = useState('')
    const [selectedComplaints, setSelectedComplaints] = useState()
    const [selectedDiagnosis, setSelectedDiagnosis] = useState()
    const [selectedPrimaryClinicalDiagnosis, setSelectedPrimaryClinicalDiagnosis] = useState(null) // Single ClinicalDiagnosis object
    const [selectedDifferentialClinicalDiagnoses, setSelectedDifferentialClinicalDiagnoses] = useState([])
    const [nextVisitInputValue, setNextVisitInputValue] = useState()
    const [nextVisitInputType, setNextVisitInputType] = useState('days')

    const [showNextVisitSlotSelector, setShowNextVisitSlotSelector] = useState(false)


    const roomId = useCheckedInOrPeekedInChamberRoomId()

    const [therapyReportTemplate, setTherapyReportTemplate] = useState()

    const [userEditing, setUserEditing] = useState(false)

    const lastSavedState = useRef({}) // Store last saved state

    const notesRef = useRef(null)

    const emrNextVisitComponent = useRef(null)
    
    // Settings state for section visibility
    const [sectionSettings, setSectionSettings] = useState(null)
    const settingsOpenRef = useRef(null)

    const [autoSaveStatus, setAutoSaveStatus] = useState({ text: '', status: 'idle' })
    const noNextVisitDialog = usePromiseDialog()

    const [nextVisitOmissionReason, setNextVisitOmissionReason] = useState('')
    const [nextVisitHoldSlotOmissionReason, setNextVisitHoldSlotOmissionReason] = useState('')

    const [isMarkingAsCompleted, setIsMarkingAsCompleted] = useState(false)
    const [hasVersionConflict, setHasVersionConflict] = useState(false)
    const [hasHydratedTherapyData, setHasHydratedTherapyData] = useState(false)

    const [primaryTherapyType, setPrimaryTherapyType] = useState(null) // Single therapy type
    const [additionalTherapyTypes, setAdditionalTherapyTypes] = useState([]) // Array of additional therapy types

    const latestTherapyReportDataRef = useRef(therapyReportData)
    const latestObservationAndAdviceRef = useRef(observationAndAdvice)
    const latestNotesRef = useRef(notes)
    const latestSelectedComplaintsRef = useRef(selectedComplaints)
    const latestSelectedDiagnosisRef = useRef(selectedDiagnosis)
    const latestSelectedPrimaryClinicalDiagnosisRef = useRef(selectedPrimaryClinicalDiagnosis)
    const latestSelectedDifferentialClinicalDiagnosesRef = useRef(selectedDifferentialClinicalDiagnoses)
    const latestNextVisitInputValueRef = useRef(nextVisitInputValue)
    const latestNextVisitInputTypeRef = useRef(nextVisitInputType)
    const latestNextVisitOmissionReasonRef = useRef(nextVisitOmissionReason)
    const latestNextVisitHoldSlotOmissionReasonRef = useRef(nextVisitHoldSlotOmissionReason)
    const latestPrimaryTherapyTypeRef = useRef(primaryTherapyType)
    const latestAdditionalTherapyTypesRef = useRef(additionalTherapyTypes)
    const latestTherapyReportIdRef = useRef(therapyReportId)
    const activeSaveRequestRef = useRef(null)
    const pendingSaveRequestRef = useRef(null)
    const nextSaveRequestIdRef = useRef(0)
    const latestQueuedSaveRequestIdRef = useRef(0)
    const activeNetworkRequestRef = useRef(0)
    const hydratedTherapyEventIdRef = useRef(null)

    const { setFlowId, setButtonTopOffset } = useContext(DocSideContext)
    const therapyDraftStorageKey = useMemo(() => {
      if (!eventId) {
        return null
      }

      return `therapy-report-draft:${eventId}`
    }, [eventId])

    const therapyDate = dayjs
      .utc(propBookedEventInfo?.startTime)
      .local()
      .format('YYYY-MM-DD HH:mm:ss')

    // Memoize the consolidated data to prevent unnecessary re-renders
    // IMPORTANT: Always create a new object reference to ensure React detects changes
    const consilidatedTherapyReportDataWithFormData = useMemo(() => {
      // For preview, we need to keep the full objects with label/title, not just submission format
      // So we'll pass the raw form state directly for preview, but use submission format for API
      // Primary Clinical Diagnosis is a single object, Differential is an array
      const primaryClinicalForPreview = selectedPrimaryClinicalDiagnosis || null
      const differentialClinicalForPreview = Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0
        ? selectedDifferentialClinicalDiagnoses
        : []
      
      // For submission: Primary is single object, Differential is array
      const primaryClinicalForSubmission = getClinicalDiagnosisForSubmission(selectedPrimaryClinicalDiagnosis, clinicId)
      const differentialClinicalForSubmission = getClinicalDiagnosisArrayForSubmission(selectedDifferentialClinicalDiagnoses, clinicId)
      
      // For preview, we need full objects with label/title, not just submission format
      // Use the new structure: primaryTherapyType (single) and additionalTherapyTypes (array)
      const originalStatus = therapyReportData?.status || 'draft'
      const finalStatus = originalStatus
      
      // Always create a new object to ensure React detects changes
      // Spread therapyReportData first, then override with form state
      const consolidated = {
        ...(therapyReportData || {}),
        // If user is editing, always set to draft. Otherwise preserve original status
        // This ensures completed reports become draft when edited
        status: finalStatus,
        mentorId: mentorProfileData?.id,
        patientId: patientId,
        bookedEventId: eventId,
        content: notes || '', // Ensure content is always a string
        id: therapyReportId ?? null,
        observation: observationAndAdvice?.observation ?? null,
        advice: observationAndAdvice?.advice ?? null,
        // Use form state directly for preview (has label/value), submission format for API
        complaints: getComplaintsArrayForSubmission(selectedComplaints, clinicId).map(complaint => ({
          ...complaint,
          apiParam: 'health-complaint__NOTE',
        })),
        diagnosis: getDiagnosisArrayForSubmission(selectedDiagnosis, clinicId).map(diagnosis => ({
          ...diagnosis,
          apiParam: 'diagnosis__NOTE',
        })),
        primaryClinicalDiagnosis: primaryClinicalForPreview,
        differentialClinicalDiagnoses: differentialClinicalForPreview,
        // New structure: therapyType (single complete object) and additionalTherapyTypes (array of complete objects)
        // For preview, use complete objects with all properties
        // Override apiParam for consistency (same as save functions)
        therapyType: primaryTherapyType ? {
          ...primaryTherapyType,
          // Ensure id and title are present for display
          id: primaryTherapyType.id || primaryTherapyType.value,
          title: primaryTherapyType.title || primaryTherapyType.label,
          // Override apiParam for save API (consistent with save functions)
          apiParam: 'therapy-type__NOTE',
          // Include note for preview
          note: primaryTherapyType.note || null,
        } : null,
        additionalTherapyTypes: Array.isArray(additionalTherapyTypes) && additionalTherapyTypes.length > 0
          ? additionalTherapyTypes.map(t => ({
              ...t,
              // Ensure id and title are present for display
              id: t.id || t.value,
              title: t.title || t.label,
              // Override apiParam for save API (consistent with save functions)
              apiParam: 'therapy-type__NOTE',
              // Include note for preview
              note: t.note || null,
            }))
          : [],
        nextVisitDate: getNextVisitDateForSubmission(
          nextVisitInputValue,
          nextVisitInputType,
          dayjs.utc(bookedEventInfo?.startTime).local(),
        ),
        nextVisitDateType: nextVisitInputType,
        nextVisitOmissionReason,
        nextVisitHoldSlotOmissionReason: nextVisitHoldSlotOmissionReason,
        nextVisitBookedEventId: bookedEventInfo?.nextVisitReferenceBookedEventId || null,
      }
      
      return consolidated
    }, [
      therapyReportData,
      userEditing, // Include userEditing so status updates when user edits
      mentorProfileData?.id,
      patientId,
      eventId,
      notes,
      therapyReportId,
      observationAndAdvice?.observation,
      observationAndAdvice?.advice,
      selectedComplaints,
      selectedDiagnosis,
      selectedPrimaryClinicalDiagnosis,
      selectedDifferentialClinicalDiagnoses,
      primaryTherapyType,
      additionalTherapyTypes,
      clinicId,
      nextVisitInputValue,
      nextVisitInputType,
      bookedEventInfo?.startTime,
      bookedEventInfo?.nextVisitReferenceBookedEventId,
      nextVisitOmissionReason,
      nextVisitHoldSlotOmissionReason,
    ])

    useEffect(() => {
      latestTherapyReportDataRef.current = therapyReportData
    }, [therapyReportData])

    useEffect(() => {
      latestObservationAndAdviceRef.current = observationAndAdvice
    }, [observationAndAdvice])

    useEffect(() => {
      latestNotesRef.current = notes
    }, [notes])

    useEffect(() => {
      latestSelectedComplaintsRef.current = selectedComplaints
    }, [selectedComplaints])

    useEffect(() => {
      latestSelectedDiagnosisRef.current = selectedDiagnosis
    }, [selectedDiagnosis])

    useEffect(() => {
      latestSelectedPrimaryClinicalDiagnosisRef.current = selectedPrimaryClinicalDiagnosis
    }, [selectedPrimaryClinicalDiagnosis])

    useEffect(() => {
      latestSelectedDifferentialClinicalDiagnosesRef.current = selectedDifferentialClinicalDiagnoses
    }, [selectedDifferentialClinicalDiagnoses])

    useEffect(() => {
      latestNextVisitInputValueRef.current = nextVisitInputValue
    }, [nextVisitInputValue])

    useEffect(() => {
      latestNextVisitInputTypeRef.current = nextVisitInputType
    }, [nextVisitInputType])

    useEffect(() => {
      latestNextVisitOmissionReasonRef.current = nextVisitOmissionReason
    }, [nextVisitOmissionReason])

    useEffect(() => {
      latestNextVisitHoldSlotOmissionReasonRef.current = nextVisitHoldSlotOmissionReason
    }, [nextVisitHoldSlotOmissionReason])

    useEffect(() => {
      latestPrimaryTherapyTypeRef.current = primaryTherapyType
    }, [primaryTherapyType])

    useEffect(() => {
      latestAdditionalTherapyTypesRef.current = additionalTherapyTypes
    }, [additionalTherapyTypes])

    useEffect(() => {
      latestTherapyReportIdRef.current = therapyReportId
    }, [therapyReportId])

    // Helper to compare values (handles null, undefined, objects, arrays)
    const valuesEqual = useCallback((a, b) => {
      if (a === b) return true
      if (a == null && b == null) return true
      if (a == null || b == null) return false
      try {
        return JSON.stringify(a) === JSON.stringify(b)
      } catch {
        return false
      }
    }, [])

    const latestValueRefsBySetter = useMemo(
      () =>
        new Map([
          [setObservationAndAdvice, latestObservationAndAdviceRef],
          [setNotes, latestNotesRef],
          [setSelectedComplaints, latestSelectedComplaintsRef],
          [setSelectedDiagnosis, latestSelectedDiagnosisRef],
          [setSelectedPrimaryClinicalDiagnosis, latestSelectedPrimaryClinicalDiagnosisRef],
          [setSelectedDifferentialClinicalDiagnoses, latestSelectedDifferentialClinicalDiagnosesRef],
          [setNextVisitInputValue, latestNextVisitInputValueRef],
          [setNextVisitInputType, latestNextVisitInputTypeRef],
          [setNextVisitOmissionReason, latestNextVisitOmissionReasonRef],
          [setNextVisitHoldSlotOmissionReason, latestNextVisitHoldSlotOmissionReasonRef],
          [setPrimaryTherapyType, latestPrimaryTherapyTypeRef],
          [setAdditionalTherapyTypes, latestAdditionalTherapyTypesRef],
          [setTherapyReportData, latestTherapyReportDataRef],
        ]),
      [],
    )

    const buildComparableTherapyState = useCallback((snapshot) => {
      const nutritionSection =
        snapshot?.nutritionSection ?? snapshot?.therapyReportData?.nutritionSection ?? null
      return {
        observationAndAdvice: snapshot?.observationAndAdvice || {
          advice: null,
          observation: null,
        },
        nutritionSection,
        notes: snapshot?.notes || '',
        selectedComplaints: snapshot?.selectedComplaints,
        selectedDiagnosis: snapshot?.selectedDiagnosis,
        nextVisitInputValue: snapshot?.nextVisitInputValue,
        nextVisitInputType: snapshot?.nextVisitInputType || 'days',
        nextVisitOmissionReason: snapshot?.nextVisitOmissionReason || '',
        nextVisitHoldSlotOmissionReason: snapshot?.nextVisitHoldSlotOmissionReason || '',
        primaryTherapyType: snapshot?.primaryTherapyType || null,
        additionalTherapyTypes: Array.isArray(snapshot?.additionalTherapyTypes)
          ? snapshot.additionalTherapyTypes
          : [],
        selectedPrimaryClinicalDiagnosis: snapshot?.selectedPrimaryClinicalDiagnosis || null,
        selectedDifferentialClinicalDiagnoses: Array.isArray(
          snapshot?.selectedDifferentialClinicalDiagnoses,
        )
          ? snapshot.selectedDifferentialClinicalDiagnoses
          : [],
      }
    }, [])

    const currentComparableState = useMemo(
      () =>
        buildComparableTherapyState({
          observationAndAdvice,
          notes,
          selectedComplaints,
          selectedDiagnosis,
          nextVisitInputValue,
          nextVisitInputType,
          nextVisitOmissionReason,
          nextVisitHoldSlotOmissionReason,
          primaryTherapyType,
          additionalTherapyTypes,
          selectedPrimaryClinicalDiagnosis,
          selectedDifferentialClinicalDiagnoses,
          therapyReportData,
        }),
      [
        additionalTherapyTypes,
        buildComparableTherapyState,
        nextVisitHoldSlotOmissionReason,
        nextVisitInputType,
        nextVisitInputValue,
        nextVisitOmissionReason,
        notes,
        observationAndAdvice,
        primaryTherapyType,
        selectedComplaints,
        selectedDiagnosis,
        selectedDifferentialClinicalDiagnoses,
        selectedPrimaryClinicalDiagnosis,
        therapyReportData,
      ],
    )

    const getCurrentTherapySnapshot = useCallback(() => {
      const currentTherapyReportData = latestTherapyReportDataRef.current || null
      return {
        therapyReportData: currentTherapyReportData,
        therapyReportId:
          latestTherapyReportIdRef.current ?? currentTherapyReportData?.id ?? null,
        nutritionSection: currentTherapyReportData?.nutritionSection ?? null,
        observationAndAdvice: latestObservationAndAdviceRef.current || {
          advice: null,
          observation: null,
        },
        notes: latestNotesRef.current || '',
        selectedComplaints: latestSelectedComplaintsRef.current,
        selectedDiagnosis: latestSelectedDiagnosisRef.current,
        selectedPrimaryClinicalDiagnosis:
          latestSelectedPrimaryClinicalDiagnosisRef.current || null,
        selectedDifferentialClinicalDiagnoses: Array.isArray(
          latestSelectedDifferentialClinicalDiagnosesRef.current,
        )
          ? latestSelectedDifferentialClinicalDiagnosesRef.current
          : [],
        nextVisitInputValue: latestNextVisitInputValueRef.current,
        nextVisitInputType: latestNextVisitInputTypeRef.current || 'days',
        nextVisitOmissionReason: latestNextVisitOmissionReasonRef.current || '',
        nextVisitHoldSlotOmissionReason:
          latestNextVisitHoldSlotOmissionReasonRef.current || '',
        primaryTherapyType: latestPrimaryTherapyTypeRef.current || null,
        additionalTherapyTypes: Array.isArray(latestAdditionalTherapyTypesRef.current)
          ? latestAdditionalTherapyTypesRef.current
          : [],
      }
    }, [])

    const hasStateChanged = useCallback(() => {
      if (!lastSavedState.current) {
        return true
      }

      const currentComparableState = buildComparableTherapyState(getCurrentTherapySnapshot())
      return !valuesEqual(currentComparableState, lastSavedState.current)
    }, [buildComparableTherapyState, getCurrentTherapySnapshot, valuesEqual])

    const currentTherapyDraftPayload = useMemo(() => {
      if (!therapyDraftStorageKey) {
        return null
      }

      return {
        schemaVersion: 1,
        bookedEventId: eventId,
        patientId,
        therapyReportId: therapyReportId ?? null,
        baseVersionNumber: therapyReportData?.versionNumber ?? null,
        baseStatus: therapyReportData?.status ?? null,
        savedAt: dayjs().toISOString(),
        comparableState: currentComparableState,
      }
    }, [
      currentComparableState,
      eventId,
      patientId,
      therapyDraftStorageKey,
      therapyReportData?.status,
      therapyReportData?.versionNumber,
      therapyReportId,
    ])

    const clearStoredTherapyDraft = useCallback(() => {
      if (!therapyDraftStorageKey || typeof window === 'undefined') {
        return
      }

      try {
        sessionStorage.removeItem(therapyDraftStorageKey)
      } catch (error) {
        console.warn('Unable to clear local therapy draft', error)
      }
    }, [therapyDraftStorageKey])

    const getStoredTherapyDraft = useCallback(() => {
      if (!therapyDraftStorageKey || typeof window === 'undefined') {
        return null
      }

      try {
        const rawDraft = sessionStorage.getItem(therapyDraftStorageKey)
        if (!rawDraft) {
          return null
        }

        const parsedDraft = JSON.parse(rawDraft)
        if (!parsedDraft?.comparableState || parsedDraft?.bookedEventId !== eventId) {
          sessionStorage.removeItem(therapyDraftStorageKey)
          return null
        }

        if (patientId && parsedDraft?.patientId && parsedDraft.patientId !== patientId) {
          sessionStorage.removeItem(therapyDraftStorageKey)
          return null
        }

        return parsedDraft
      } catch (error) {
        console.warn('Unable to read local therapy draft', error)
        try {
          sessionStorage.removeItem(therapyDraftStorageKey)
        } catch {}
        return null
      }
    }, [eventId, patientId, therapyDraftStorageKey])

    const applyEditableTherapyState = useCallback(
      (editableState) => {
        const normalizedState = buildComparableTherapyState(editableState)

        setTherapyReportData((prev) => {
          const nextReportData = {
            ...(prev ?? {}),
            nutritionSection: normalizedState.nutritionSection ?? null,
          }
          latestTherapyReportDataRef.current = nextReportData
          return nextReportData
        })
        setObservationAndAdvice(normalizedState.observationAndAdvice)
        setSelectedComplaints(normalizedState.selectedComplaints)
        setSelectedDiagnosis(normalizedState.selectedDiagnosis)
        setSelectedPrimaryClinicalDiagnosis(normalizedState.selectedPrimaryClinicalDiagnosis)
        setSelectedDifferentialClinicalDiagnoses(
          normalizedState.selectedDifferentialClinicalDiagnoses,
        )
        setNextVisitInputValue(normalizedState.nextVisitInputValue)
        setNotes(normalizedState.notes)
        setNextVisitInputType(normalizedState.nextVisitInputType)
        setNextVisitOmissionReason(normalizedState.nextVisitOmissionReason)
        setNextVisitHoldSlotOmissionReason(normalizedState.nextVisitHoldSlotOmissionReason)
        setPrimaryTherapyType(normalizedState.primaryTherapyType)
        setAdditionalTherapyTypes(normalizedState.additionalTherapyTypes)

        latestObservationAndAdviceRef.current = normalizedState.observationAndAdvice
        latestSelectedComplaintsRef.current = normalizedState.selectedComplaints
        latestSelectedDiagnosisRef.current = normalizedState.selectedDiagnosis
        latestSelectedPrimaryClinicalDiagnosisRef.current =
          normalizedState.selectedPrimaryClinicalDiagnosis
        latestSelectedDifferentialClinicalDiagnosesRef.current =
          normalizedState.selectedDifferentialClinicalDiagnoses
        latestNextVisitInputValueRef.current = normalizedState.nextVisitInputValue
        latestNotesRef.current = normalizedState.notes
        latestNextVisitInputTypeRef.current = normalizedState.nextVisitInputType
        latestNextVisitOmissionReasonRef.current = normalizedState.nextVisitOmissionReason
        latestNextVisitHoldSlotOmissionReasonRef.current =
          normalizedState.nextVisitHoldSlotOmissionReason
        latestPrimaryTherapyTypeRef.current = normalizedState.primaryTherapyType
        latestAdditionalTherapyTypesRef.current = normalizedState.additionalTherapyTypes
      },
      [buildComparableTherapyState],
    )

    function formatTherapyTypeForSave(therapyType) {
      if (!therapyType) {
        return null
      }

      return {
        ...therapyType,
        id: therapyType.id || therapyType.value,
        title: therapyType.title || therapyType.label || therapyType.value,
        apiParam: 'therapy-type__NOTE',
        note: therapyType.note || null,
      }
    }

    function normalizeSaveOptions(...args) {
      if (
        args.length === 1 &&
        args[0] &&
        typeof args[0] === 'object' &&
        !Array.isArray(args[0])
      ) {
        return {
          mode: args[0].mode || 'save',
          hideToast: Boolean(args[0].hideToast),
          refreshBookedEvent: Boolean(args[0].refreshBookedEvent),
        }
      }

      if (args.length === 0) {
        return {
          mode: 'save',
          hideToast: false,
          refreshBookedEvent: false,
        }
      }

      const [hideToast = false, isRefreshingData = true, isMarkingAsCompleted = false] = args
      return {
        mode: isMarkingAsCompleted ? 'complete' : 'draft',
        hideToast: Boolean(hideToast),
        refreshBookedEvent: Boolean(isRefreshingData),
      }
    }

    function getTherapySavePriority(mode = 'save') {
      if (mode === 'autosave') {
        return 1
      }

      if (['complete', 'issue', 'leave-complete'].includes(mode)) {
        return 3
      }

      return 2
    }

    function resolveTherapyStatusForMode(mode, currentStatus) {
      if (['complete', 'issue', 'leave-complete'].includes(mode)) {
        return 'completed'
      }

      if (['draft', 'leave-draft', 'print-draft', 'pdf-draft'].includes(mode)) {
        return 'draft'
      }

      return currentStatus === 'completed' ? 'completed' : 'draft'
    }

    function extractTherapyReportFromResponse(response) {
      return (
        response?.data?.therapyReport ||
        getDataMapFromCommonResponse(response)?.therapyReport ||
        response?.response?.data?.therapyReport ||
        response?.response?.data?.data?.therapyReport ||
        response?.data?.data?.therapyReport
      )
    }

    function extractTherapySaveErrorMessage(response, fallback = "Couldn't save therapy report.") {
      return (
        response?.response?.data?.message ||
        response?.data?.message ||
        response?.response?.data?.data?.cause ||
        response?.message ||
        fallback
      )
    }

    function createTherapySaveError(message, extra = {}) {
      const error = new Error(message)
      Object.assign(error, extra)
      return error
    }

    function syncSavedTherapyMetadata(savedTherapyReport) {
      if (!savedTherapyReport) {
        return
      }

      setTherapyReportData((prev) => ({
        ...(prev || {}),
        ...savedTherapyReport,
      }))
      setTherapyReportId(savedTherapyReport?.id || null)
    }

    async function syncSavedTherapyFormData(savedTherapyReport) {
      if (!savedTherapyReport) {
        return
      }

      await processAndSetTherapyPageData(savedTherapyReport)
    }

    function scheduleAutoSaveStatusClear(statuses = ['saved'], delay = 3000) {
      setTimeout(() => {
        setAutoSaveStatus((prev) => {
          if (statuses.includes(prev.status)) {
            return { text: '', status: 'idle' }
          }
          return prev
        })
      }, delay)
    }

    function handleTherapyVersionConflict(response, mode, hideToast = false) {
      const serverTherapyReport = extractTherapyReportFromResponse(response)
      const message =
        extractTherapySaveErrorMessage(
          response,
          'A newer saved version is already on the server. Your latest text is still on screen. Please save again when ready.',
        )

      setHasVersionConflict(true)
      setAutoSaveStatus({ text: message, status: 'error' })

      if (serverTherapyReport) {
        syncSavedTherapyMetadata(serverTherapyReport)
      }

      if (mode !== 'autosave' && !hideToast) {
        toast.error(message)
      }

      return createTherapySaveError(message, {
        skipRetry: true,
        isVersionConflict: true,
        autoSaveStatusText: message,
        therapyReport: serverTherapyReport,
      })
    }

    async function executeTherapySaveRequest(request) {
      const snapshot = getCurrentTherapySnapshot()
      const comparableState = buildComparableTherapyState(snapshot)
      const isAutoSaveRequest = request.mode === 'autosave'
      const isCompletionSave = ['complete', 'issue', 'leave-complete'].includes(request.mode)

      if (isCompletionSave && !isNutritionDiscipline && !snapshot.primaryTherapyType) {
        const message = 'Please select Therapy Type for this Patient before completing the session'
        if (!request.hideToast) {
          toast.error(message)
        }
        throw createTherapySaveError(message, {
          skipRetry: true,
          autoSaveStatusText: message,
        })
      }

      if (notesRef.current?.saveNote) {
        notesRef.current.saveNote()
      }

      activeNetworkRequestRef.current = request.id

      const therapyReportPayload = {
        bookedEventId: eventId,
        patientId,
        mentorId: mentorProfileData?.id,
        id: snapshot.therapyReportId ?? null,
        versionNumber: snapshot?.therapyReportData?.versionNumber ?? null,
        status: resolveTherapyStatusForMode(request.mode, snapshot?.therapyReportData?.status),
        discipline: therapyDiscipline,
        nutritionSection: snapshot.nutritionSection ?? null,
        content: snapshot.notes,
        freeFormRichText: snapshot.notes || '',
        observation: snapshot.observationAndAdvice?.observation ?? null,
        advice: snapshot.observationAndAdvice?.advice ?? null,
        complaints: getComplaintsArrayForSubmission(
          snapshot.selectedComplaints,
          clinicId,
        ).map((complaint) => ({
          ...complaint,
          apiParam: 'health-complaint__NOTE',
        })),
        diagnosis: getDiagnosisArrayForSubmission(snapshot.selectedDiagnosis, clinicId).map(
          (diagnosis) => ({
            ...diagnosis,
            apiParam: 'diagnosis__NOTE',
          }),
        ),
        primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(
          snapshot.selectedPrimaryClinicalDiagnosis,
          clinicId,
        ),
        differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(
          snapshot.selectedDifferentialClinicalDiagnoses,
          clinicId,
        ),
        therapyType: formatTherapyTypeForSave(snapshot.primaryTherapyType),
        additionalTherapyTypes: Array.isArray(snapshot.additionalTherapyTypes)
          ? snapshot.additionalTherapyTypes
              .map((therapyType) => formatTherapyTypeForSave(therapyType))
              .filter(Boolean)
          : [],
        nextVisitDate: getNextVisitDateForSubmission(
          snapshot.nextVisitInputValue,
          snapshot.nextVisitInputType,
          dayjs.utc(bookedEventInfo?.startTime).local(),
        ),
        nextVisitDateType: snapshot.nextVisitInputType,
        nextVisitOmissionReason: snapshot.nextVisitOmissionReason,
        nextVisitHoldSlotOmissionReason: snapshot.nextVisitHoldSlotOmissionReason,
        nextVisitBookedEventId: bookedEventInfo?.nextVisitReferenceBookedEventId || null,
      }

      const body = {
        therapyReport: therapyReportPayload,
        emrSectionDataList: [buildFreeFormRichTextEmrSection(snapshot.notes || '')],
      }

      const response = await postMPDataWithoutParams(
        isAutoSaveRequest
          ? '/api/secure/patient/therapy-report/emr/autosave'
          : '/api/secure/patient/therapy-report/emr/save',
        body,
      )

      const responseStatus = response?.status ?? response?.response?.status

      if (responseStatus === 409) {
        throw handleTherapyVersionConflict(response, request.mode, request.hideToast)
      }

      if (responseStatus !== 200) {
        const message = extractTherapySaveErrorMessage(response)

        if (!isAutoSaveRequest) {
          setAutoSaveStatus({ text: 'Save Failed', status: 'error' })
          if (!request.hideToast) {
            toast.error(message)
          }
        }

        throw createTherapySaveError(message, {
          autoSaveStatusText: message,
        })
      }

      const savedTherapyReport = extractTherapyReportFromResponse(response)
      const hasNewerQueuedRequest = latestQueuedSaveRequestIdRef.current > request.id
      const hasLocalChangesSinceRequest = !valuesEqual(
        buildComparableTherapyState(getCurrentTherapySnapshot()),
        comparableState,
      )
      const shouldApplyCurrentResponse = !hasNewerQueuedRequest && !hasLocalChangesSinceRequest

      setHasVersionConflict(false)
      if (savedTherapyReport) {
        syncSavedTherapyMetadata(savedTherapyReport)
      }

      if (shouldApplyCurrentResponse) {
        lastSavedState.current = comparableState
        setUserEditing(false)

        if (!isAutoSaveRequest && savedTherapyReport && !request.refreshBookedEvent) {
          await syncSavedTherapyFormData(savedTherapyReport)
        }
      }

      if (request.refreshBookedEvent && shouldApplyCurrentResponse && getProcessAndSetEventData) {
        await getProcessAndSetEventData({ hideLoader: true })
      }

      if (!isAutoSaveRequest && shouldApplyCurrentResponse) {
        setAutoSaveStatus({ text: 'Report Saved', status: 'saved' })
        scheduleAutoSaveStatusClear()
        if (!request.hideToast) {
          toast.success(`Saved ${getTherapyReportLabel(therapyDiscipline)}`)
        }
      }

      return {
        skipped: false,
        response,
        savedTherapyReport,
        isCurrent: shouldApplyCurrentResponse,
      }
    }

    async function processTherapySaveQueue() {
      if (!activeSaveRequestRef.current) {
        return
      }

      setPrescriptionSaveLoading(true)

      while (activeSaveRequestRef.current) {
        const request = activeSaveRequestRef.current

        try {
          const result = await executeTherapySaveRequest(request)
          request.resolve(result)
        } catch (error) {
          request.reject(error)
        }

        const nextRequest = pendingSaveRequestRef.current
        pendingSaveRequestRef.current = null
        activeSaveRequestRef.current = nextRequest
      }

      setPrescriptionSaveLoading(false)
    }

    function enqueueTherapySave(options) {
      if (options.mode !== 'autosave') {
        resetResilientAutoSave()
      }

      return new Promise((resolve, reject) => {
        const request = {
          id: nextSaveRequestIdRef.current + 1,
          ...options,
          resolve,
          reject,
        }
        nextSaveRequestIdRef.current = request.id
        latestQueuedSaveRequestIdRef.current = request.id

        if (!activeSaveRequestRef.current) {
          activeSaveRequestRef.current = request
          void processTherapySaveQueue()
          return
        }

        const pendingRequest = pendingSaveRequestRef.current
        if (!pendingRequest) {
          pendingSaveRequestRef.current = request
          return
        }

        if (getTherapySavePriority(request.mode) >= getTherapySavePriority(pendingRequest.mode)) {
          pendingRequest.resolve({
            skipped: true,
            supersededBy: request.mode,
          })
          pendingSaveRequestRef.current = request
          return
        }

        resolve({
          skipped: true,
          supersededBy: pendingRequest.mode,
        })
      })
    }

    async function saveTherapyReport(...args) {
      const options = normalizeSaveOptions(...args)
      return enqueueTherapySave(options)
    }

    const performAutoSave = useCallback(async () => {
      const result = await saveTherapyReport({
        mode: 'autosave',
        hideToast: true,
        refreshBookedEvent: false,
      })
      return result
    }, [saveTherapyReport])

    // Use resilient auto-save hook
    const {
      isAutoSaving,
      autoSaveStatus: resilientAutoSaveStatus,
      retryAttempt,
      nextRetryDelay,
      triggerAutoSave,
      cancelRetry,
      resetAutoSave: resetResilientAutoSave,
    } = useResilientAutoSave(
      performAutoSave,
      hasStateChanged,
      {
        initialDelay: 1500,
        initialRetryDelay: 30000, // 30 seconds
        maxRetryDelay: 300000, // 5 minutes
        backoffMultiplier: 2,
        maxRetries: Infinity,
        enabled: true,
        resetOnSuccess: true,
        onSaveSuccess: () => {
          setAutoSaveStatus({ text: 'Saved', status: 'saved' })
          scheduleAutoSaveStatusClear()
        },
        onSaveError: (error, attempt) => {
          if (attempt === 0) {
            setAutoSaveStatus({
              text: error?.autoSaveStatusText || 'Save Failed',
              status: 'error',
            })
          } else {
            const nextDelay = 30000 * Math.pow(2, attempt - 1)
            const maxDelay = Math.min(nextDelay, 300000)
            setAutoSaveStatus({
              text:
                error?.autoSaveStatusText ||
                `Save Failed (retrying in ${Math.round(maxDelay / 1000)}s)`,
              status: 'error',
            })
          }
          console.error('Auto-save failed:', error)
        },
        onRetry: (attempt, delay) => {
          setAutoSaveStatus({
            text: `Retrying save (attempt ${attempt}) in ${Math.round(delay / 1000)}s...`,
            status: 'retrying',
          })
        },
      }
    )

    // Sync resilient auto-save status with local state for UI
    useEffect(() => {
      if (resilientAutoSaveStatus.status === 'retrying') {
        setAutoSaveStatus(resilientAutoSaveStatus)
      }
    }, [resilientAutoSaveStatus])

    function setUserEditingState(setStateFunction, setStateVal) {
      const latestValueRef = latestValueRefsBySetter.get(setStateFunction)
      if (latestValueRef) {
        latestValueRef.current =
          typeof setStateVal === 'function'
            ? setStateVal(latestValueRef.current)
            : setStateVal
      }

      setUserEditing(true)
      setStateFunction(setStateVal)

      requestAnimationFrame(() => {
        setTimeout(() => {
          triggerAutoSave()
        }, 200)
      })
    }

    async function getAndSetMentorProfileData() {
      if (propMentorProfileData) {
        setMentorProfileData(propMentorProfileData)
        return propMentorProfileData
      } else {
        setMentorProfileDataLoading(true)
        const resp = await getMPData('/api/secure/teacher/profile')
        setMentorProfileDataLoading(false)
        if (resp?.status === 200) {
          setMentorProfileData(resp?.data)
        }
        return resp?.data
      }
    }

    async function processAndSetTherapyPageData(reportData) {
      const mainData = reportData || {}
      const nextObservationAndAdvice = {
        advice: mainData?.advice || null,
        observation: mainData?.observation || null,
      }
      const nextSelectedComplaints = mainData?.complaints
        ? getInitialDataForSettingExistingSelectedComplaints(mainData.complaints)
        : undefined
      const nextSelectedDiagnosis = mainData?.diagnosis
        ? getInitialDataForSettingExistingSelectedDiagnosis(mainData.diagnosis)
        : undefined
      const nextSelectedPrimaryClinicalDiagnosis = mainData?.primaryClinicalDiagnosis
        ? getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis(
            mainData.primaryClinicalDiagnosis,
          )
        : null
      const nextSelectedDifferentialClinicalDiagnoses =
        mainData?.differentialClinicalDiagnoses
          ? getInitialDataForSettingExistingSelectedClinicalDiagnosis(
              mainData.differentialClinicalDiagnoses,
            )
          : []
      const nextNextVisitInputValue = mainData?.nextVisitDate
        ? getInitialDataForSettingDaysOfNextVisit(
            mainData.nextVisitDate,
            therapyDate,
            mainData?.nextVisitDateType,
          )
        : undefined
      const nextNotes = mainData?.freeFormRichText || mainData?.content || ''
      const nextNextVisitInputType = mainData?.nextVisitDateType || 'days'
      const nextNextVisitOmissionReason = mainData?.nextVisitOmissionReason || ''
      const nextNextVisitHoldSlotOmissionReason =
        mainData?.nextVisitHoldSlotOmissionReason || ''

      let nextPrimaryTherapyType = null
      let nextAdditionalTherapyTypes = []

      if (mainData?.therapyType) {
        nextPrimaryTherapyType =
          getInitialDataForSettingExistingSelectedTherapiesForTherapyReport([
            mainData.therapyType,
          ])[0] || null
        nextAdditionalTherapyTypes =
          mainData?.additionalTherapyTypes && Array.isArray(mainData.additionalTherapyTypes)
            ? getInitialDataForSettingExistingSelectedTherapiesForTherapyReport(
                mainData.additionalTherapyTypes,
              )
            : []
      } else if (mainData?.therapyTypes) {
        const therapyTypesArray =
          getInitialDataForSettingExistingSelectedTherapiesForTherapyReport(
            mainData.therapyTypes,
          )
        nextPrimaryTherapyType = therapyTypesArray[0] || null
        nextAdditionalTherapyTypes = therapyTypesArray.slice(1)
      }

      const serverComparableState = buildComparableTherapyState({
        observationAndAdvice: nextObservationAndAdvice,
        notes: nextNotes,
        selectedComplaints: nextSelectedComplaints,
        selectedDiagnosis: nextSelectedDiagnosis,
        nextVisitInputValue: nextNextVisitInputValue,
        nextVisitInputType: nextNextVisitInputType,
        nextVisitOmissionReason: nextNextVisitOmissionReason,
        nextVisitHoldSlotOmissionReason: nextNextVisitHoldSlotOmissionReason,
        primaryTherapyType: nextPrimaryTherapyType,
        additionalTherapyTypes: nextAdditionalTherapyTypes,
        nutritionSection: mainData?.nutritionSection ?? null,
        selectedPrimaryClinicalDiagnosis: nextSelectedPrimaryClinicalDiagnosis,
        selectedDifferentialClinicalDiagnoses: nextSelectedDifferentialClinicalDiagnoses,
      })
      const storedDraft = getStoredTherapyDraft()
      const storedComparableState = storedDraft?.comparableState
        ? buildComparableTherapyState(storedDraft.comparableState)
        : null
      const shouldRestoreLocalDraft =
        Boolean(storedComparableState) &&
        !valuesEqual(storedComparableState, serverComparableState)

      setTherapyReportData(mainData)
      setTherapyReportId(mainData?.id || null)
      setHasVersionConflict(false)
      setHasHydratedTherapyData(true)
      hydratedTherapyEventIdRef.current = eventId
      setUserEditing(shouldRestoreLocalDraft)
      latestTherapyReportDataRef.current = mainData
      latestTherapyReportIdRef.current = mainData?.id || null
      applyEditableTherapyState(
        shouldRestoreLocalDraft ? storedComparableState : serverComparableState,
      )

      lastSavedState.current = serverComparableState

      if (shouldRestoreLocalDraft) {
        const recoveryMessage = 'Recovered unsaved local draft. Re-syncing in background.'
        setAutoSaveStatus({ text: recoveryMessage, status: 'recovered' })
        scheduleAutoSaveStatusClear(['recovered'], 6000)
        toast.info(recoveryMessage)
        setTimeout(() => {
          triggerAutoSave()
        }, 400)
      } else {
        clearStoredTherapyDraft()
      }
    }

    async function getAndSetTherapyReportData() {
      let therapyReportData
      let templateData

      if (auxiliaryDataFromProps) {
        therapyReportData = auxiliaryDataFromProps?.therapyReport
        templateData = auxiliaryDataFromProps?.therapyReportTemplate
      }
      if (!therapyReportData || !templateData) {
        const response = await getMPDataWithoutParams(
          `/api/secure/patient/therapy-report/create-by-eventId?eventId=${eventId}`,
        )
        if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
          therapyReportData = response.data.data.therapyReport
          templateData = response.data.data.therapyReportTemplate
        } else {
          toast.error('Error getting therapy report data')
        }
      }
      processAndSetTherapyPageData(therapyReportData)
      setTherapyReportTemplate(templateData)
    }

    function renderInfoText() {
      if (autoSaveStatus.status === 'idle') {
        return <></>
      }

      console.log({ prescriptionSaveLoading })

      if (prescriptionSaveLoading) {
        return (
          <Box
            sx={{
              color: 'unFocused.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Cached fontSize="18px" />
            <span>Saving</span>
          </Box>
        )
      }

      // Handle retrying status
      if (autoSaveStatus.status === 'retrying') {
        return (
          <Box
            sx={{
              color: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Cached fontSize="18px" />
            <span>{autoSaveStatus.text}</span>
          </Box>
        )
      }

      if (autoSaveStatus.status === 'recovered') {
        return (
          <Box
            sx={{
              color: 'info.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Info fontSize="18px" />
            <span>{autoSaveStatus.text}</span>
          </Box>
        )
      }

      return autoSaveStatus.status === 'error' ? (
        <Box
          sx={{
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Info fontSize="18px" />
          <span>{autoSaveStatus.text}</span>
        </Box>
      ) : (
        <Box
          sx={{
            color: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Check fontSize="18px" />
          <span>{autoSaveStatus.text}</span>
        </Box>
      )
    }

    async function getEMRMeta() {
      const response = await getMPDataWithParams(`/api/secure/teacher/next-visit-widget/get`, {
        'event-id': eventId,
      })
      if (response.status === 200) {
        if (isCommonResponseSuccessfulV2(response)) {
          const nextVisitString = getDataMapFromCommonResponse(response)?.['next-visit-widget']
          const shouldShowNextVisitSlotSelector = nextVisitString === 'bookingWidget'
          setShowNextVisitSlotSelector(shouldShowNextVisitSlotSelector)
        }
      }
    }

    async function setup() {
      getEMRMeta()
      const response = await getAndSetMentorProfileData()
      if (eventId && patientId) {
        getAndSetTherapyReportData()
      } else {
        navigate('/404')
      }
    }

    async function saveAndLeaveTherapySession(forceMarkAsCompleted = true) {
      const markingAsCompleted = forceMarkAsCompleted === true
      try {
        await saveTherapyReport({
          mode: markingAsCompleted ? 'leave-complete' : 'leave-draft',
          hideToast: true,
          refreshBookedEvent: false,
        })

        if (leaveRoom) {
          await leaveRoom(markingAsCompleted)
        }
      } catch (error) {
        throw error
      }
    }

    async function checkAndOpenModal(markAsCompleted) {
      if (markAsCompleted === undefined || markAsCompleted === null) {
        markAsCompleted = true
      }

      if (markAsCompleted && !isNutritionDiscipline && !latestPrimaryTherapyTypeRef.current) {
        toast.error('Please select Therapy Type for this Patient before completing the session')
        return FLOW_STATUSES_FOR_LEAVING_SESSION.stop
      }

      const validationParams = {
        markAsCompleted,
        nextVisitInputValue: latestNextVisitInputValueRef.current,
        showNextVisitSlotSelector,
        nextVisitReferenceBookedEventId: bookedEventInfo?.nextVisitReferenceBookedEventId,
        nextVisitHoldSlotOmissionReason: latestNextVisitHoldSlotOmissionReasonRef.current,
      }
      const validationResult = validateNextVisitBeforeLeaving(validationParams)

      switch (validationResult.action) {
        case VALIDATION_RESULT.ALLOW_LEAVE:
          await saveTherapyReport({
            mode: markAsCompleted === false ? 'leave-draft' : 'leave-complete',
            hideToast: true,
            refreshBookedEvent: false,
          })
          return FLOW_STATUSES_FOR_LEAVING_SESSION.continue

        case VALIDATION_RESULT.SHOW_NO_NEXT_VISIT_DIALOG:
          try {
            await noNextVisitDialog.show()
            await saveTherapyReport({
              mode: markAsCompleted === false ? 'leave-draft' : 'leave-complete',
              hideToast: true,
              refreshBookedEvent: false,
            })
            return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
          } catch (error) {
            return FLOW_STATUSES_FOR_LEAVING_SESSION.stop
          }

        case VALIDATION_RESULT.SHOW_HOLD_SLOT_DIALOG:
          if (emrNextVisitComponent?.current) {
            setIsMarkingAsCompleted(markAsCompleted)
            try {
              await emrNextVisitComponent.current.confirmOmissionReason()
              await saveTherapyReport({
                mode: markAsCompleted === false ? 'leave-draft' : 'leave-complete',
                hideToast: true,
                refreshBookedEvent: false,
              })
              return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
            } catch (error) {
              return FLOW_STATUSES_FOR_LEAVING_SESSION.stop
            }
          } else {
            await saveTherapyReport({
              mode: markAsCompleted === false ? 'leave-draft' : 'leave-complete',
              hideToast: true,
              refreshBookedEvent: false,
            })
            return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
          }

        default:
          return FLOW_STATUSES_FOR_LEAVING_SESSION.stop
      }
    }

    useImperativeHandle(ref, () => ({
      checkAndOpenModal,
      saveTherapyReport,
      saveAndPrint,
      printReport,
      prescriptionSaveLoading,
      isAutoSaving,
      autoSaveStatus: resilientAutoSaveStatus,
      isSaving: prescriptionSaveLoading || isAutoSaving,
      therapyReportStatus: consilidatedTherapyReportDataWithFormData?.status || therapyReportData?.status || null,
      getLatestReportData: () => ({
        report: consilidatedTherapyReportDataWithFormData,
        therapyReportEmrSections,
      }),
      openSettings: () => {
        if (settingsOpenRef.current) {
          settingsOpenRef.current()
        }
      },
    }))

    async function saveAndPrint() {
      await saveTherapyReport({
        mode: 'print-draft',
        hideToast: false,
        refreshBookedEvent: false,
      })
      if (previewPrintRef.current?.print) {
        await previewPrintRef.current.print()
        return
      }
      toast.error('Report preview is still loading. Please try printing again.')
    }
    
    async function printReport() {
      // Just print without saving - only works for completed reports
      const reportStatus = consilidatedTherapyReportDataWithFormData?.status || therapyReportData?.status
      console.log('🔍 printReport called - reportStatus:', reportStatus, 'consolidated status:', consilidatedTherapyReportDataWithFormData?.status, 'therapyReportData status:', therapyReportData?.status)
      
      if (reportStatus === 'completed') {
        // Use the preview component's print function
        console.log('🔍 Calling previewPrintRef.current.print()')
        if (previewPrintRef.current?.print) {
          await previewPrintRef.current.print()
        } else {
          console.warn('⚠️ previewPrintRef.current.print is not available')
          toast.error('Report preview is still loading. Please try printing again.')
        }
      } else {
        console.log('⚠️ Report status is not completed:', reportStatus, '- falling back to saveAndPrint')
        // If not completed, fall back to save and print
        await saveAndPrint()
      }
    }

    async function saveAndGeneratePDF(type) {
      await saveTherapyReport({
        mode: 'pdf-draft',
        hideToast: false,
        refreshBookedEvent: false,
      })
      
      // Format filename as: <PatientNumber>_Therapy_Report_<startTime>
      const patientNumber = therapyReportData?.patientNumber || 'Unknown'
      let startTimeFormatted = ''
      
      if (bookedEventInfo?.startTime) {
        // Format as YYYYMMDD_HHmmss for filename (no spaces or special chars)
        startTimeFormatted = dayjs.utc(bookedEventInfo.startTime).local().format('YYYYMMDD_HHmmss')
      } else if (therapyReportData?.createdTime) {
        // Fallback to createdTime if bookedEventInfo not available
        startTimeFormatted = dayjs.utc(therapyReportData.createdTime).local().format('YYYYMMDD_HHmmss')
      } else {
        // Final fallback to current time
        startTimeFormatted = dayjs().format('YYYYMMDD_HHmmss')
      }
      
      const filename = `${patientNumber}_Therapy_Report_${startTimeFormatted}.pdf`
      
      if (type === 'open') {
        generatePDF(targetRef, {
          filename: filename,
          method: 'open',
          page: { margin: Margin.MEDIUM },
        })
      } else {
        generatePDF(targetRef, {
          filename: filename,
          method: 'save',
          page: { margin: Margin.MEDIUM },
        })
      }
    }

    async function saveTriggerFunction() {
      if (triggerSave) {
        await saveTherapyReport({
          mode: 'draft',
          hideToast: false,
          refreshBookedEvent: false,
        })
        if (setTriggerSave) {
          setTriggerSave(false)
        }
        if (afterSaveFunction) {
          afterSaveFunction()
        }
      }
    }

    // Create a key that changes when form data changes to force preview re-render
    // Include ALL fields that affect the preview to ensure it updates when any field changes
    const previewKey = useMemo(() => {
      // Create a hash of all fields that affect the preview
      const keyData = {
        therapyTypeId: primaryTherapyType?.id || primaryTherapyType?.value,
        notesLength: notes?.length || 0,
        notesHash: notes?.substring(0, 100) || '',
        observationHash: observationAndAdvice?.observation?.substring(0, 100) || '',
        adviceHash: observationAndAdvice?.advice?.substring(0, 100) || '',
        additionalTherapyTypesCount: additionalTherapyTypes?.length || 0,
        complaintsCount: Array.isArray(selectedComplaints) ? selectedComplaints.length : 0,
        complaintsHash: JSON.stringify(selectedComplaints)?.substring(0, 100) || '',
        diagnosisCount: Array.isArray(selectedDiagnosis) ? selectedDiagnosis.length : 0,
        diagnosisHash: JSON.stringify(selectedDiagnosis)?.substring(0, 100) || '',
        primaryClinicalDiagnosisId: selectedPrimaryClinicalDiagnosis?.id || selectedPrimaryClinicalDiagnosis?.value || null,
        differentialClinicalDiagnosesCount: Array.isArray(selectedDifferentialClinicalDiagnoses) ? selectedDifferentialClinicalDiagnoses.length : 0,
        differentialClinicalDiagnosesHash: JSON.stringify(selectedDifferentialClinicalDiagnoses)?.substring(0, 100) || '',
        nextVisitInputValue: nextVisitInputValue,
        nextVisitInputType: nextVisitInputType,
        nextVisitOmissionReason: nextVisitOmissionReason,
        nextVisitHoldSlotOmissionReason: nextVisitHoldSlotOmissionReason,
        therapyReportId: therapyReportId,
      }
      return JSON.stringify(keyData)
    }, [
      primaryTherapyType, 
      notes, 
      observationAndAdvice, 
      additionalTherapyTypes, 
      selectedComplaints,
      selectedDiagnosis,
      selectedPrimaryClinicalDiagnosis,
      selectedDifferentialClinicalDiagnoses,
      nextVisitInputValue,
      nextVisitInputType,
      nextVisitOmissionReason,
      nextVisitHoldSlotOmissionReason,
      therapyReportId
    ])

    // Create a fresh object reference for the preview to ensure React detects changes
    // This ensures the preview component receives a new object reference when form data changes
    const reportForPreview = useMemo(() => {
      return { ...consilidatedTherapyReportDataWithFormData }
    }, [consilidatedTherapyReportDataWithFormData])

    function reportPreview({ hiddenInUI = false } = { hiddenInUI: false }) {
      return (
        <TherapyReportPreview
          key={previewKey}
          ref={previewPrintRef}
          hideInUI={hiddenInUI}
          isAlwaysPrinting={true}
          report={reportForPreview}
          templateData={therapyReportTemplate}
          setReport={setTherapyReportData}
          therapyDate={therapyDate}
          bookedEventInfo={bookedEventInfo}
          hideHeader
          onIssueReport={async () => {
            try {
              if (!therapyReportId) {
                await saveTherapyReport({
                  mode: 'draft',
                  hideToast: true,
                  refreshBookedEvent: false,
                })
              }

              await saveTherapyReport({
                mode: 'issue',
                hideToast: false,
                refreshBookedEvent: true,
              })
            } catch (error) {
              console.error('Error in onIssueReport:', error)
              throw error
            }
          }}
        />
      )
      // return (
      //   <PMPParent hiddenInUI={hiddenInUI}>
      //     <TherapyReportHeader
      //       therapyReportData={therapyReportData}
      //       templateData={therapyReportTemplate}
      //       therapyDate={therapyDate}
      //     />
      //     <div style={{ flex: 1, padding: '20px' }}>
      //       <TherapyReportBodyFirstSection
      //         headingText={'Therapy Report'}
      //         therapyReportData={{
      //           ...therapyReportData,
      //           createdTime: therapyReportData?.createdTime || therapyDate,
      //         }}
      //         observationAndAdvice={observationAndAdvice}
      //         selectedComplaints={selectedComplaints}
      //         selectedDiagnosis={selectedDiagnosis}
      //       />
      //       <TherapyReportBody
      //         observationAndAdvice={observationAndAdvice}
      //         content={notes}
      //         nextVisitInputType={nextVisitInputType}
      //         nextVisitInputValue={nextVisitInputValue}
      //         therapyDate={therapyDate}
      //       />
      //     </div>
      //     <TherapyReportFooter therapyReportData={therapyReportData} />
      //   </PMPParent>
      // )
    }

    // // Debounced version of saveTherapyReport
    // // const debouncedSaveTherapyReport = useCallback(
    // //   _.debounce(() => saveTherapyReport(true), 1500),
    // //   [],
    // // )
    // const debouncedSaveTherapyReport = useMemo(
    //   () => _.debounce(() => saveTherapyReport(true), 1500),
    //   [],
    // )
    
    // Cleanup is handled by useResilientAutoSave hook

    // SIMPLIFIED DESIGN: Child component does NOT manage header state
    // Parent component (MentorClinicSomeChamberAutoJoiningPage) is the single source of truth
    // This component just renders its content - header is managed by parent based on selectedOptionPatientMenu
    // Removed all header-setting logic to prevent race conditions

    useEffect(() => {
      setup()
    }, [eventId, patientId, auxiliaryDataFromProps])

    useEffect(() => {
      setHasHydratedTherapyData(false)
      hydratedTherapyEventIdRef.current = null
    }, [eventId, patientId])

    useEffect(() => {
      saveTriggerFunction()
    }, [triggerSave])

    useEffect(() => {
      if (
        !hasHydratedTherapyData ||
        hydratedTherapyEventIdRef.current !== eventId ||
        !therapyDraftStorageKey ||
        !currentTherapyDraftPayload
      ) {
        return
      }

      if (valuesEqual(currentComparableState, lastSavedState.current)) {
        clearStoredTherapyDraft()
        return
      }

      const timeoutId = setTimeout(() => {
        try {
          sessionStorage.setItem(
            therapyDraftStorageKey,
            JSON.stringify(currentTherapyDraftPayload),
          )
        } catch (error) {
          console.warn('Unable to persist therapy draft locally', error)
        }
      }, 300)

      return () => clearTimeout(timeoutId)
    }, [
      clearStoredTherapyDraft,
      currentComparableState,
      currentTherapyDraftPayload,
      hasHydratedTherapyData,
      therapyDraftStorageKey,
      valuesEqual,
    ])

    useEffect(() => {
      if (!hasHydratedTherapyData || hydratedTherapyEventIdRef.current !== eventId) {
        return
      }

      const handleBeforeUnload = (event) => {
        const hasUnsavedChanges = hasStateChanged()
        const hasSaveInFlight =
          prescriptionSaveLoading ||
          isAutoSaving ||
          Boolean(activeSaveRequestRef.current) ||
          Boolean(pendingSaveRequestRef.current)

        if (!hasUnsavedChanges && !hasSaveInFlight) {
          return
        }

        event.preventDefault()
        event.returnValue = ''
      }

      window.addEventListener('beforeunload', handleBeforeUnload)
      return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [hasHydratedTherapyData, hasStateChanged, isAutoSaving, prescriptionSaveLoading])

    // Initialize lastSavedState for new reports (when no data exists yet)
    useEffect(() => {
      // Only initialize if lastSavedState is empty and we have some state values
      if (!lastSavedState.current || Object.keys(lastSavedState.current).length === 0) {
        // Wait a bit for state to be set from processAndSetTherapyPageData
        const timeoutId = setTimeout(() => {
          // Only initialize if still empty (processAndSetTherapyPageData might have set it)
          if (!lastSavedState.current || Object.keys(lastSavedState.current).length === 0) {
            lastSavedState.current = buildComparableTherapyState({
              observationAndAdvice,
              notes,
              selectedComplaints,
              selectedDiagnosis,
              nextVisitInputValue,
              nextVisitInputType,
              nextVisitOmissionReason,
              nextVisitHoldSlotOmissionReason,
              primaryTherapyType,
              additionalTherapyTypes,
              therapyReportData,
              selectedPrimaryClinicalDiagnosis,
              selectedDifferentialClinicalDiagnoses,
            })
          }
        }, 1000)
        return () => clearTimeout(timeoutId)
      }
    }, [therapyReportData, buildComparableTherapyState]) // Only run when therapyReportData changes (on initial load)

    // Watch for changes in clinical diagnosis fields and trigger autosave
    // This ensures that nested fields like onsetTime, durationSince, durationType are detected
    // Use a ref to track previous values to avoid unnecessary triggers
    const prevClinicalDiagnosisRef = useRef({
      primary: null,
      differential: null,
    })
    
    useEffect(() => {
      // Only trigger if lastSavedState has been initialized
      if (!lastSavedState.current || Object.keys(lastSavedState.current).length === 0) {
        // Update ref with current values but don't trigger autosave yet
        prevClinicalDiagnosisRef.current = {
          primary: selectedPrimaryClinicalDiagnosis,
          differential: selectedDifferentialClinicalDiagnoses,
        }
        return
      }
      
      // Compare current state with previous ref values to detect actual changes
      const hasChanged = 
        !valuesEqual(selectedPrimaryClinicalDiagnosis, prevClinicalDiagnosisRef.current.primary) ||
        !valuesEqual(selectedDifferentialClinicalDiagnoses, prevClinicalDiagnosisRef.current.differential)
      
      if (hasChanged) {
        // Update ref with current values
        prevClinicalDiagnosisRef.current = {
          primary: selectedPrimaryClinicalDiagnosis,
          differential: selectedDifferentialClinicalDiagnoses,
        }
        
        // Trigger autosave with a delay to ensure state has updated
        const timeoutId = setTimeout(() => {
          triggerAutoSave()
        }, 500)
        return () => clearTimeout(timeoutId)
      }
    }, [selectedPrimaryClinicalDiagnosis, selectedDifferentialClinicalDiagnoses, valuesEqual, triggerAutoSave])

    // Helper function to check if section is visible
    const isSectionVisible = useCallback(
      (sectionId) => {
        if (!sectionSettings) return true // Default to visible if settings not loaded
        const section = sectionSettings.find((s) => s.id === sectionId)
        return section ? section.visible : true
      },
      [sectionSettings],
    )

    // Get sections in order from settings
    const getSectionsInOrder = useCallback(() => {
      if (!sectionSettings) {
        // Return default order if settings not loaded
        return [
          'patient-details',
          'therapy-history',
          'consultation-history',
          'type-of-therapy',
          'additional-therapy',
          'observations',
          'complaints',
          'diagnosis',
          'primary-clinical-diagnosis',
          'differential-clinical-diagnoses',
          'advice',
          'free-form-report',
          'documents-upload',
          'next-visit',
          'private-notes',
        ]
      }
      // Sort by order and return section IDs - respects user's custom order
      return [...sectionSettings]
        .sort((a, b) => a.order - b.order)
        .map((s) => s.id)
    }, [sectionSettings])

    // Render a section by its ID
    const renderSection = useCallback((sectionId) => {
      switch (sectionId) {
        case 'patient-details':
          return (
            <SectionWrapper
              key="patient-details"
              sectionId="patient-details"
              isVisible={isSectionVisible('patient-details')}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.98) 100%)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  border: '1px solid rgba(99, 102, 241, 0.1)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.6) 0%, rgba(5, 150, 105, 0.6) 100%)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    marginBottom: 1,
                    paddingBottom: 0.75,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  }}
                >
                  <Box
                    sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                      color: '#10b981',
                    }}
                  >
                    <Person sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#1f2937',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    }}
                  >
                    Patient Details
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 1,
                    marginTop: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      padding: '8px 10px',
                      borderRadius: '8px',
                      background: 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Patient Name
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1f2937',
                      }}
                    >
                      {therapyReportData?.patientName}
                    </Typography>
                    {therapyReportData?.patientNumber && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#9ca3af',
                          fontFamily: 'monospace',
                        }}
                      >
                        #{therapyReportData?.patientNumber}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      padding: '8px 10px',
                      borderRadius: '8px',
                      background: 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Age / Gender
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1f2937',
                      }}
                    >
                      {getAgeFromDobWithTime(therapyReportData?.patientDateOfBirth)} / {
                      GENDER_LIST_ONBOARDING?.find(
                        (item) => item.value === therapyReportData?.patientGender,
                        )?.label || 'N/A'
                      }
                    </Typography>
                  </Box>
                  {(() => {
                    // Calculate visit number
                    const visitNumber = bookedEventInfo?.patientMeetingSeqNumber || 
                      (auxiliaryDataFromProps?.patientVisitSummary?.sessionsCompletedForCurrentEventTypeForCurrentMentor 
                        ? auxiliaryDataFromProps.patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor + 1 
                        : 1)
                    const getOrdinalSuffix = (n) => {
                      const s = ['th', 'st', 'nd', 'rd']
                      const v = n % 100
                      return n + (s[(v - 20) % 10] || s[v] || s[0])
                    }
                    const visitNumberText = visitNumber ? getOrdinalSuffix(visitNumber) : null
                    
                    // Format time slot
                    const timeSlot = bookedEventInfo?.startTime && bookedEventInfo?.endTime
                      ? `${dayjs.utc(bookedEventInfo.startTime).local().format('h:mma')} - ${dayjs.utc(bookedEventInfo.endTime).local().format('h:mma')}`
                      : bookedEventInfo?.startTime
                      ? dayjs.utc(bookedEventInfo.startTime).local().format('h:mma')
                      : null
                    
                    return (
                      <>
                        {visitNumberText && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              padding: '8px 10px',
                              borderRadius: '8px',
                              background: 'rgba(0, 0, 0, 0.02)',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                fontWeight: 600,
                                color: '#6b7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              Visit Number
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1f2937',
                              }}
                            >
                              {visitNumberText} Visit
                            </Typography>
                          </Box>
                        )}
                        {timeSlot && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                              padding: '8px 10px',
                              borderRadius: '8px',
                              background: 'rgba(0, 0, 0, 0.02)',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '10px',
                                fontWeight: 600,
                                color: '#6b7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              Time Slot
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1f2937',
                              }}
                            >
                              {timeSlot}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )
                  })()}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      padding: '8px 10px',
                      borderRadius: '8px',
                      background: 'rgba(0, 0, 0, 0.02)',
                      gridColumn: { xs: '1', sm: 'span 2' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Session Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1f2937',
                      }}
                    >
                      {dayjs.utc(therapyDate).local().format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </SectionWrapper>
          )
        
        case 'therapy-history':
        case 'consultation-history':
          // These are handled together in PatientVisitSummaryHandler
          if (sectionId === 'therapy-history') {
            return (
              <div key="therapy-consultation-history" style={{ fontSize: '16px', width: '100%', display: 'block' }}>
                <PMPSectionHeading></PMPSectionHeading>
                <div style={{ width: '100%', display: 'block' }}>
                  <PatientVisitSummaryHandler
                    patientVisitSummary={auxiliaryDataFromProps?.patientVisitSummary}
                    role="therapist"
                    showTherapyHistory={isSectionVisible('therapy-history')}
                    showConsultationHistory={isSectionVisible('consultation-history')}
                  />
                </div>
              </div>
            )
          }
          return null // consultation-history is handled with therapy-history
        
        case 'type-of-therapy':
          return (
            <SectionWrapper
              key="type-of-therapy"
              sectionId="type-of-therapy"
              isVisible={isSectionVisible('type-of-therapy')}
            >
              <ModernSectionHeading icon={PsychologyIcon}>Therapy Type for this Patient <span style={{ color: '#d32f2f' }}>*</span></ModernSectionHeading>
              <div style={{ width: '100%', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <SuggestedTherapySelectMenu
                  isMulti={false}
                  clinicId={clinicId}
                  selectedTherapies={primaryTherapyType}
                  setSelectedTherapies={(val) => {
                    // When therapy type changes, preserve note if it exists, otherwise initialize with empty string
                    const updatedVal = val ? {
                      ...val,
                      note: val.note !== undefined ? val.note : (primaryTherapyType?.note || ''),
                    } : null
                    setUserEditingState(setPrimaryTherapyType, updatedVal)
                  }}
                  styles={commonStyles}
                  closeMenuOnSelect={true}
                />
                {primaryTherapyType && (
                  <div style={{ width: '100%' }}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '8px',
                      }}
                    >
                      Notes for {primaryTherapyType?.label || primaryTherapyType?.title || 'Therapy Type'}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Enter notes for this therapy type..."
                      value={primaryTherapyType?.note || ''}
                      onChange={(e) => {
                        setUserEditingState(setPrimaryTherapyType, {
                          ...primaryTherapyType,
                          note: e.target.value,
                        })
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '14px',
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </SectionWrapper>
          )
        
        case 'additional-therapy':
          // Only show Additional Therapy Types if primary therapy type is selected
          if (!primaryTherapyType) {
            return null
          }
          return (
            <SectionWrapper
              key="additional-therapy"
              sectionId="additional-therapy"
              isVisible={isSectionVisible('additional-therapy')}
            >
              <ModernSectionHeading icon={PsychologyIcon}>Additional Therapy Types</ModernSectionHeading>
              <div style={{ width: '100%', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <SuggestedTherapySelectMenu
                  isMulti={true}
                  clinicId={clinicId}
                  selectedTherapies={additionalTherapyTypes}
                  setSelectedTherapies={(val) => {
                    // When therapy types change, preserve notes for existing items
                    const updatedVal = Array.isArray(val) ? val.map((newItem) => {
                      // Find existing item with same id/value to preserve note
                      const existingItem = additionalTherapyTypes.find(
                        (existing) => (existing?.id || existing?.value) === (newItem?.id || newItem?.value)
                      )
                      return {
                        ...newItem,
                        note: newItem.note !== undefined ? newItem.note : (existingItem?.note || ''),
                      }
                    }) : []
                    setUserEditingState(setAdditionalTherapyTypes, updatedVal)
                  }}
                  styles={commonStyles}
                />
                {Array.isArray(additionalTherapyTypes) && additionalTherapyTypes.length > 0 && (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {additionalTherapyTypes.map((therapyType, index) => (
                      <div key={therapyType?.id || therapyType?.value || index} style={{ width: '100%' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '8px',
                          }}
                        >
                          Notes for {therapyType?.label || therapyType?.title || 'Therapy Type'}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Enter notes for this therapy type..."
                          value={therapyType?.note || ''}
                          onChange={(e) => {
                            const updated = [...additionalTherapyTypes]
                            updated[index] = {
                              ...therapyType,
                              note: e.target.value,
                            }
                            setUserEditingState(setAdditionalTherapyTypes, updated)
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              fontSize: '14px',
                            },
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </SectionWrapper>
          )
        
        case 'private-notes':
          return (
            <SectionWrapper
              key="private-notes"
              sectionId="private-notes"
              isVisible={isSectionVisible('private-notes')}
              isPrivate={true}
            >
              <ModernSectionHeading icon={LockIcon} isPrivate={true}>Private Notes</ModernSectionHeading>
              <div style={{ marginTop: '4px' }}>
              <PatientNotesSectionForReports
                ref={notesRef}
                patientName={therapyReportData?.patientName}
                thisBookedEventId={eventId}
                patientId={patientId}
              />
              </div>
            </SectionWrapper>
          )
        
        case 'observations':
          return (
            <SectionWrapper
              key="observations"
              sectionId="observations"
              isVisible={isSectionVisible('observations')}
            >
              <PMPSectionHeading>Observations</PMPSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <PrescriptionTextAreaComponent
                  inputValue={observationAndAdvice?.observation}
                  setInputValue={(value) =>
                    setUserEditingState(setObservationAndAdvice, (prev) => ({
                      ...prev,
                      observation: value,
                    }))
                  }
                />
              </div>
            </SectionWrapper>
          )
        
        case 'complaints':
          return (
            <SectionWrapper
              key="complaints"
              sectionId="complaints"
              isVisible={isSectionVisible('complaints')}
            >
              <ModernSectionHeading icon={HealingIcon}>Presenting Concern</ModernSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                <PresentingComplaintsSection
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  selectedComplaints={selectedComplaints}
                  setSelectedComplaints={(val) => setUserEditingState(setSelectedComplaints, val)}
                />
              </div>
            </SectionWrapper>
          )
        
        case 'diagnosis':
          // Only show Diagnosis field if it has data already present
          const hasDiagnosisData = Array.isArray(selectedDiagnosis) && selectedDiagnosis.length > 0
          if (!hasDiagnosisData) {
            return null
          }
          return (
            <SectionWrapper
              key="diagnosis"
              sectionId="diagnosis"
              isVisible={isSectionVisible('diagnosis')}
            >
              <ModernSectionHeading icon={LocalHospitalIcon}>Diagnosis</ModernSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                <DiagnosisSelectMenu
                  clinicId={clinicId}
                  selectedDiagnosis={selectedDiagnosis}
                  setSelectedDiagnosis={(val) => setUserEditingState(setSelectedDiagnosis, val)}
                />
              </div>
            </SectionWrapper>
          )
        
        case 'primary-clinical-diagnosis':
          return (
            <SectionWrapper
              key="primary-clinical-diagnosis"
              sectionId="primary-clinical-diagnosis"
              isVisible={isSectionVisible('primary-clinical-diagnosis')}
            >
              <ModernSectionHeading icon={LocalHospitalIcon}>Primary Clinical Diagnosis</ModernSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', position: 'relative', marginTop: '4px' }}>
                <ClinicalDiagnosisSelectMenu
                  clinicId={clinicId}
                  isMulti={false}
                  selectedClinicalDiagnosis={selectedPrimaryClinicalDiagnosis}
                  setSelectedClinicalDiagnosis={(val) => {
                    // When a diagnosis is selected, initialize with empty fields if needed
                    if (val && !val.note && !val.onsetTime && !val.durationSince) {
                      setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                        ...val,
                        note: '',
                        onsetTime: null,
                        durationSince: '',
                        durationType: 'days',
                      })
                    } else {
                      setUserEditingState(setSelectedPrimaryClinicalDiagnosis, val)
                    }
                  }}
                />
                {selectedPrimaryClinicalDiagnosis && (selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value || selectedPrimaryClinicalDiagnosis?.id) && (
                  <div style={{ 
                    padding: isMobile ? '16px' : '20px', 
                    backgroundColor: '#f0f4f8', 
                    borderRadius: '12px',
                    border: '2px solid #cbd5e1',
                    marginTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    width: '100%',
                    boxSizing: 'border-box',
                    position: 'static',
                    zIndex: 'auto',
                    overflow: 'visible'
                  }}>
                    <div style={{ fontWeight: 600, fontSize: isMobile ? '14px' : '16px', color: '#1e293b', marginBottom: '8px' }}>
                      {selectedPrimaryClinicalDiagnosis.title || selectedPrimaryClinicalDiagnosis.label || 'Untitled'}
                    </div>
                    
                    {/* Note Field */}
                    <div style={{ width: '100%' }}>
                      <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                        Note
                      </PMPSectionInputLabel>
                      <PrescriptionTextAreaComponent
                        inputValue={selectedPrimaryClinicalDiagnosis.note || ''}
                        setInputValue={(value) => {
                          setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                            ...selectedPrimaryClinicalDiagnosis,
                            note: value,
                          })
                        }}
                        placeholder="Enter clinical notes..."
                      />
                    </div>

                    {/* Onset Date and Duration Fields - Responsive Layout */}
                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '12px' : '16px', 
                      alignItems: 'flex-start', 
                      flexWrap: 'wrap',
                      width: '100%'
                    }}>
                      {/* Onset Date Field */}
                      <div style={{ 
                        flex: isMobile ? '1 1 100%' : '1 1 200px', 
                        minWidth: isMobile ? '100%' : '200px',
                        width: '100%'
                      }}>
                        <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                          Onset Date
                        </PMPSectionInputLabel>
                        <DayjsDatePicker
                          date={selectedPrimaryClinicalDiagnosis.onsetTime 
                            ? dayjs.utc(selectedPrimaryClinicalDiagnosis.onsetTime).local()
                            : null
                          }
                          onChange={(newDate) => {
                            if (newDate) {
                              // Store as UTC date at midnight (YYYY-MM-DD 00:00:00)
                              // newDate is already a dayjs object in local time
                              const dateString = newDate.format('YYYY-MM-DD')
                              const utcDate = dayjs.utc(dateString).format('YYYY-MM-DD HH:mm:ss')
                              
                              // Auto-calculate duration based on onset date and therapy date
                              const therapyDate = bookedEventInfo?.startTime 
                                ? dayjs.utc(bookedEventInfo.startTime).local().startOf('day')
                                : dayjs().startOf('day')
                              const onsetDateLocal = newDate.startOf('day')
                              const daysDiff = therapyDate.diff(onsetDateLocal, 'day')
                              
                              // Determine best unit and value (only if daysDiff is positive)
                              let durationValue = null
                              let durationType = 'days'
                              
                              if (daysDiff > 0) {
                                if (daysDiff >= 365) {
                                  durationValue = Math.round(daysDiff / 365)
                                  durationType = 'years'
                                } else if (daysDiff >= 30) {
                                  durationValue = Math.round(daysDiff / 30)
                                  durationType = 'months'
                                } else if (daysDiff >= 7) {
                                  durationValue = Math.round(daysDiff / 7)
                                  durationType = 'weeks'
                                } else {
                                  durationValue = daysDiff
                                  durationType = 'days'
                                }
                              }
                              
                              setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                                ...selectedPrimaryClinicalDiagnosis,
                                onsetTime: utcDate,
                                // Auto-fill duration when onset date changes
                                durationSince: durationValue !== null ? durationValue : (selectedPrimaryClinicalDiagnosis.durationSince || null),
                                durationType: durationValue !== null ? durationType : (selectedPrimaryClinicalDiagnosis.durationType || 'days'),
                              })
                            } else {
                              setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                                ...selectedPrimaryClinicalDiagnosis,
                                onsetTime: null,
                              })
                            }
                          }}
                          format="DD MMM YYYY"
                          placeholder="Select onset date..."
                          buttonProps={{
                            fullWidth: true,
                            sx: {
                              backgroundColor: '#ffffff',
                              borderColor: '#e5e7eb',
                              color: '#1e293b',
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: '#6366f1',
                                backgroundColor: '#ffffff',
                              },
                            },
                          }}
                        />
                      </div>

                      {/* Duration Fields */}
                      <div style={{ 
                        flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 120px', 
                        minWidth: isMobile ? 'calc(50% - 6px)' : '120px',
                        width: isMobile ? 'calc(50% - 6px)' : 'auto'
                      }}>
                        <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                          Duration
                        </PMPSectionInputLabel>
                        <TextField
                          type="number"
                          fullWidth
                          value={selectedPrimaryClinicalDiagnosis.durationSince || ''}
                          onChange={(e) => {
                            const value = e.target.value
                            setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                              ...selectedPrimaryClinicalDiagnosis,
                              durationSince: value ? parseInt(value, 10) : null,
                            })
                          }}
                          inputProps={{ min: 0 }}
                          variant="outlined"
                          placeholder="0"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                              '&:hover fieldset': {
                                borderColor: '#6366f1',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#6366f1',
                              },
                            },
                          }}
                        />
                      </div>
                      <div style={{ 
                        flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 130px', 
                        minWidth: isMobile ? 'calc(50% - 6px)' : '130px',
                        width: isMobile ? 'calc(50% - 6px)' : 'auto'
                      }}>
                        <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                          Duration Type
                        </PMPSectionInputLabel>
                        <TextField
                          select
                          fullWidth
                          value={selectedPrimaryClinicalDiagnosis.durationType || 'days'}
                          onChange={(e) => {
                            setUserEditingState(setSelectedPrimaryClinicalDiagnosis, {
                              ...selectedPrimaryClinicalDiagnosis,
                              durationType: e.target.value,
                            })
                          }}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                              '&:hover fieldset': {
                                borderColor: '#6366f1',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#6366f1',
                              },
                            },
                          }}
                        >
                          <MenuItem value="days">Days</MenuItem>
                          <MenuItem value="weeks">Weeks</MenuItem>
                          <MenuItem value="months">Months</MenuItem>
                          <MenuItem value="years">Years</MenuItem>
                        </TextField>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </SectionWrapper>
          )
        
        case 'differential-clinical-diagnoses':
          // Show if there are differential diagnoses OR if primary clinical diagnosis is present
          const hasDifferentialDiagnoses = Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0
          const hasPrimaryDiagnosis = selectedPrimaryClinicalDiagnosis && (selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value || selectedPrimaryClinicalDiagnosis?.id)
          
          if (!hasDifferentialDiagnoses && !hasPrimaryDiagnosis) {
            return null
          }
          
          return (
            <SectionWrapper
              key="differential-clinical-diagnoses"
              sectionId="differential-clinical-diagnoses"
              isVisible={isSectionVisible('differential-clinical-diagnoses')}
            >
              <ModernSectionHeading icon={LocalHospitalIcon}>Differential Clinical Diagnoses</ModernSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', position: 'relative' }}>
                <ClinicalDiagnosisSelectMenu
                  clinicId={clinicId}
                  isMulti={true}
                  selectedClinicalDiagnosis={selectedDifferentialClinicalDiagnoses}
                  setSelectedClinicalDiagnosis={(val) => {
                    // When diagnoses are selected, initialize with empty fields if needed
                    if (Array.isArray(val)) {
                      const initialized = val.map(diagnosis => {
                        if (diagnosis && !diagnosis.note && !diagnosis.onsetTime && !diagnosis.durationSince) {
                          return {
                            ...diagnosis,
                            note: '',
                            onsetTime: null,
                            durationSince: '',
                            durationType: 'days',
                          }
                        }
                        return diagnosis
                      })
                      setUserEditingState(setSelectedDifferentialClinicalDiagnoses, initialized)
                    } else {
                      setUserEditingState(setSelectedDifferentialClinicalDiagnoses, val)
                    }
                  }}
                />
                {Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px', width: '100%' }}>
                    {selectedDifferentialClinicalDiagnoses.map((diagnosis, index) => {
                      // Only render if diagnosis has a valid identifier
                      if (!diagnosis || (!diagnosis?.title && !diagnosis?.label && !diagnosis?.value && !diagnosis?.id)) {
                        return null
                      }
                      return (
                      <div 
                        key={`differential-${index}-${diagnosis.id || diagnosis.value || index}`}
                        style={{ 
                          padding: isMobile ? '16px' : '20px', 
                          backgroundColor: '#f0f4f8', 
                          borderRadius: '12px',
                          border: '2px solid #cbd5e1',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                          width: '100%',
                          boxSizing: 'border-box',
                          position: 'static',
                          zIndex: 'auto',
                          overflow: 'visible'
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: isMobile ? '14px' : '16px', color: '#1e293b', marginBottom: '8px' }}>
                          {diagnosis.title || diagnosis.label || 'Untitled'}
                        </div>
                        
                        {/* Note Field */}
                        <div style={{ width: '100%' }}>
                          <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Note
                          </PMPSectionInputLabel>
                          <PrescriptionTextAreaComponent
                            inputValue={diagnosis.note || ''}
                            setInputValue={(value) => {
                              const updated = [...selectedDifferentialClinicalDiagnoses]
                              updated[index] = {
                                ...diagnosis,
                                note: value,
                              }
                              setUserEditingState(setSelectedDifferentialClinicalDiagnoses, updated)
                            }}
                            placeholder="Enter clinical notes..."
                          />
                        </div>

                        {/* Onset Date and Duration Fields - Responsive Layout */}
                        <div style={{ 
                          display: 'flex', 
                          gap: isMobile ? '12px' : '16px', 
                          alignItems: 'flex-start', 
                          flexWrap: 'wrap',
                          width: '100%'
                        }}>
                          {/* Onset Date Field */}
                          <div style={{ 
                            flex: isMobile ? '1 1 100%' : '1 1 200px', 
                            minWidth: isMobile ? '100%' : '200px',
                            width: '100%'
                          }}>
                            <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                              Onset Date
                            </PMPSectionInputLabel>
                            <DayjsDatePicker
                              date={diagnosis.onsetTime 
                                ? dayjs.utc(diagnosis.onsetTime).local()
                                : null
                              }
                              onChange={(newDate) => {
                                if (newDate) {
                                  // Store as UTC date at midnight (YYYY-MM-DD 00:00:00)
                                  const dateString = newDate.format('YYYY-MM-DD')
                                  const utcDate = dayjs.utc(dateString).format('YYYY-MM-DD HH:mm:ss')
                                  
                                  // Auto-calculate duration based on onset date and therapy date
                                  const therapyDate = bookedEventInfo?.startTime 
                                    ? dayjs.utc(bookedEventInfo.startTime).local().startOf('day')
                                    : dayjs().startOf('day')
                                  const onsetDateLocal = newDate.startOf('day')
                                  const daysDiff = therapyDate.diff(onsetDateLocal, 'day')
                                  
                                  // Determine best unit and value (only if daysDiff is positive)
                                  let durationValue = null
                                  let durationType = 'days'
                                  
                                  if (daysDiff > 0) {
                                    if (daysDiff >= 365) {
                                      durationValue = Math.round(daysDiff / 365)
                                      durationType = 'years'
                                    } else if (daysDiff >= 30) {
                                      durationValue = Math.round(daysDiff / 30)
                                      durationType = 'months'
                                    } else if (daysDiff >= 7) {
                                      durationValue = Math.round(daysDiff / 7)
                                      durationType = 'weeks'
                                    } else {
                                      durationValue = daysDiff
                                      durationType = 'days'
                                    }
                                  }
                                  
                                  const updated = [...selectedDifferentialClinicalDiagnoses]
                                  updated[index] = {
                                    ...diagnosis,
                                    onsetTime: utcDate,
                                    // Auto-fill duration when onset date changes
                                    durationSince: durationValue !== null ? durationValue : (diagnosis.durationSince || null),
                                    durationType: durationValue !== null ? durationType : (diagnosis.durationType || 'days'),
                                  }
                                  setUserEditingState(setSelectedDifferentialClinicalDiagnoses, updated)
                                } else {
                                  const updated = [...selectedDifferentialClinicalDiagnoses]
                                  updated[index] = {
                                    ...diagnosis,
                                    onsetTime: null,
                                  }
                                  setUserEditingState(setSelectedDifferentialClinicalDiagnoses, updated)
                                }
                              }}
                              format="DD MMM YYYY"
                              placeholder="Select onset date..."
                              buttonProps={{
                                fullWidth: true,
                                sx: {
                                  backgroundColor: '#ffffff',
                                  borderColor: '#e5e7eb',
                                  color: '#1e293b',
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                  '&:hover': {
                                    borderColor: '#6366f1',
                                    backgroundColor: '#ffffff',
                                  },
                                },
                              }}
                            />
                          </div>

                          {/* Duration Fields */}
                          <div style={{ 
                            flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 120px', 
                            minWidth: isMobile ? 'calc(50% - 6px)' : '120px',
                            width: isMobile ? 'calc(50% - 6px)' : 'auto'
                          }}>
                            <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                              Duration
                            </PMPSectionInputLabel>
                            <TextField
                              type="number"
                              fullWidth
                              value={diagnosis.durationSince || ''}
                              onChange={(e) => {
                                const value = e.target.value
                                const updated = [...selectedDifferentialClinicalDiagnoses]
                                updated[index] = {
                                  ...diagnosis,
                                  durationSince: value ? parseInt(value, 10) : null,
                                }
                                setUserEditingState(setSelectedDifferentialClinicalDiagnoses, updated)
                              }}
                              inputProps={{ min: 0 }}
                              variant="outlined"
                              placeholder="0"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#ffffff',
                                  '&:hover fieldset': {
                                    borderColor: '#6366f1',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#6366f1',
                                  },
                                },
                              }}
                            />
                          </div>
                          <div style={{ 
                            flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 130px', 
                            minWidth: isMobile ? 'calc(50% - 6px)' : '130px',
                            width: isMobile ? 'calc(50% - 6px)' : 'auto'
                          }}>
                            <PMPSectionInputLabel style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                              Duration Type
                            </PMPSectionInputLabel>
                            <TextField
                              select
                              fullWidth
                              value={diagnosis.durationType || 'days'}
                              onChange={(e) => {
                                const updated = [...selectedDifferentialClinicalDiagnoses]
                                updated[index] = {
                                  ...diagnosis,
                                  durationType: e.target.value,
                                }
                                setUserEditingState(setSelectedDifferentialClinicalDiagnoses, updated)
                              }}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#ffffff',
                                  '&:hover fieldset': {
                                    borderColor: '#6366f1',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#6366f1',
                                  },
                                },
                              }}
                            >
                              <MenuItem value="days">Days</MenuItem>
                              <MenuItem value="weeks">Weeks</MenuItem>
                              <MenuItem value="months">Months</MenuItem>
                              <MenuItem value="years">Years</MenuItem>
                            </TextField>
                          </div>
                        </div>
                      </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </SectionWrapper>
          )
        
        case 'advice':
          return (
            <SectionWrapper
              key="advice"
              sectionId="advice"
              isVisible={isSectionVisible('advice')}
            >
              <ModernSectionHeading icon={DescriptionIcon}>Advice</ModernSectionHeading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                <PrescriptionTextAreaComponent
                  inputValue={observationAndAdvice?.advice}
                  setInputValue={(value) =>
                    setUserEditingState(setObservationAndAdvice, (prev) => ({
                      ...prev,
                      advice: value,
                    }))
                  }
                />
              </div>
            </SectionWrapper>
          )
        
        case 'free-form-report':
          return (
            <SectionWrapper
              key="free-form-report"
              sectionId="free-form-report"
              isVisible={isSectionVisible('free-form-report')}
            >
              <PMPSectionHeading>{getTherapyReportLabel(therapyDiscipline)}/Notes</PMPSectionHeading>
              <div
                style={{
                  width: '100%',
                  marginTop: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '500px',
                }}
              >
                <TipTapEditor
                  value={notes}
                  onChange={(html) => setUserEditingState(setNotes, html)}
                  placeholder="Start writing your therapy notes..."
                />
              </div>
            </SectionWrapper>
          )
        
        case 'next-visit':
          return (
            <SectionWrapper
              key="next-visit"
              sectionId="next-visit"
              isVisible={isSectionVisible('next-visit')}
            >
              <ModernSectionHeading icon={CalendarTodayIcon}>Next Visit</ModernSectionHeading>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}
              >
                <EMRNextVisitComponentModernized
                  ref={emrNextVisitComponent}
                  nextVisitInputValue={nextVisitInputValue}
                  setNextVisitInputValue={(val) => setUserEditingState(setNextVisitInputValue, val)}
                  nextVisitInputType={nextVisitInputType}
                  setNextVisitInputType={(val) => setUserEditingState(setNextVisitInputType, val)}
                  bookedEventInfoStartTime={bookedEventInfo?.startTime}
                  mentorProfile={mentorProfileData}
                  roomId={bookedEventInfo?.chamberRoomId || roomId}
                  currentEventTypeId={bookedEventInfo?.eventTypeId}
                  mentorProfileData={mentorProfileData}
                  currentPatient={useMemo(() => ({
                    id: patientId,
                    name: therapyReportData?.patientName,
                    clinicId: clinicId,
                    clinicBrandId: clinicBrandId,
                  }), [patientId, therapyReportData?.patientName, clinicId, clinicBrandId])}
                  refreshCurrentBookedEventInfoData={useCallback(async () => {
                    await getProcessAndSetEventData({ hideLoader: true })
                  }, [getProcessAndSetEventData])}
                  currentFee={bookedEventInfo?.feePaid}
                  currentCurrency={bookedEventInfo?.currency}
                  currentBookedEventId={eventId}
                  nextVisitBookedEventId={bookedEventInfo?.nextVisitReferenceBookedEventId}
                  showHoldSlotForNextVisit={showNextVisitSlotSelector}
                  saveReport={saveTherapyReport}
                  nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
                  setNextVisitHoldSlotOmissionReason={(val) =>
                    setUserEditingState(setNextVisitHoldSlotOmissionReason, val)
                  }
                  isMarkingAsCompleted={isMarkingAsCompleted}
                  leaveRoom={saveAndLeaveTherapySession}
                />
              </div>
            </SectionWrapper>
          )

        case 'documents-upload':
          return (
            <SectionWrapper
              key="documents-upload"
              sectionId="documents-upload"
              isVisible={isSectionVisible('documents-upload')}
            >
              <DocumentsUploadSection
                eventId={eventId}
                bookedEventInfo={bookedEventInfo}
                therapyReportData={therapyReportData}
                mentorProfileData={mentorProfileData}
              />
            </SectionWrapper>
          )
        
        default:
          return null
      }
    }, [
      isSectionVisible,
      therapyReportData,
      therapyDate,
      primaryTherapyType,
      setPrimaryTherapyType,
      additionalTherapyTypes,
      setAdditionalTherapyTypes,
      notesRef,
      eventId,
      patientId,
      observationAndAdvice,
      setUserEditingState,
      selectedComplaints,
      selectedDiagnosis,
      notes,
      setNotes,
      nextVisitInputValue,
      nextVisitInputType,
      emrNextVisitComponent,
      bookedEventInfo,
      mentorProfileData,
      roomId,
      clinicId,
      clinicBrandId,
      getProcessAndSetEventData,
      showNextVisitSlotSelector,
      saveTherapyReport,
      nextVisitHoldSlotOmissionReason,
      isMarkingAsCompleted,
      saveAndLeaveTherapySession,
      auxiliaryDataFromProps?.patientVisitSummary,
      selectedPrimaryClinicalDiagnosis,
      selectedDifferentialClinicalDiagnoses,
    ])

    // Preserve scroll position when section visibility changes
    const previousSectionSettingsRef = useRef(sectionSettings)
    const scrollPositionRef = useRef({ top: 0, left: 0 })

    useEffect(() => {
      if (!sectionSettings) {
        previousSectionSettingsRef.current = sectionSettings
        return
      }

      // Check if section visibility actually changed
      const hasVisibilityChanged = previousSectionSettingsRef.current && 
        JSON.stringify(
          previousSectionSettingsRef.current.map(s => ({ id: s.id, visible: s.visible }))
        ) !== JSON.stringify(
          sectionSettings.map(s => ({ id: s.id, visible: s.visible }))
        )

      if (hasVisibilityChanged) {
        // Store current scroll position before DOM changes
        const getScrollContainer = () => {
          return document.querySelector('.LSMainContent') || 
                 document.querySelector('.PrescriptionTabFinalParent') ||
                 document.documentElement ||
                 document.body
        }

        const scrollContainer = getScrollContainer()
        scrollPositionRef.current = {
          top: scrollContainer === document.documentElement || scrollContainer === document.body
            ? window.scrollY || document.documentElement.scrollTop
            : scrollContainer.scrollTop,
          left: scrollContainer === document.documentElement || scrollContainer === document.body
            ? window.scrollX || document.documentElement.scrollLeft
            : scrollContainer.scrollLeft,
        }

        // Restore scroll position after DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const container = getScrollContainer()
            if (container === document.documentElement || container === document.body) {
              window.scrollTo({
                top: scrollPositionRef.current.top,
                left: scrollPositionRef.current.left,
                behavior: 'auto',
              })
            } else {
              container.scrollTo({
                top: scrollPositionRef.current.top,
                left: scrollPositionRef.current.left,
                behavior: 'auto',
              })
            }
          })
        })
      }

      previousSectionSettingsRef.current = sectionSettings
    }, [sectionSettings])

    if (mentorProfileDataLoading) {
      return <LoadingSpinner />
    }

    if (isNutritionDiscipline) {
      // Nutrition discipline reuses the therapy-report pipeline (save endpoints, IDs, next-visit,
      // documents) but renders the nutrition EMR section set instead of psychotherapy-specific
      // sections. Keeps the patient-details strip and documents/next-visit blocks consistent.
      return (
        <PrescriptionTabFinalParent style={{ width: '100%' }}>
          <SEOComponent
            title={`Nutrition_consultation_${therapyReportData?.patientNumber}_${dayjs
              .utc(therapyDate)
              .local()
              .format('YYYYMMDD_HHmmss')}`}
          />
          <div
            className="hide-on-print-only"
            style={{
              margin: '0 auto',
              width: '95%',
              backgroundColor: 'rgba(240, 253, 244, 0.4)',
              minHeight: '100vh',
              padding: '24px',
            }}
          >
            {renderSection('patient-details')}
            <NutritionConsultationSections
              therapyReportData={therapyReportData}
              setTherapyReportData={(nextValue) =>
                setUserEditingState(setTherapyReportData, nextValue)
              }
              emrSections={therapyReportEmrSections}
              reportKey={therapyReportId}
              bookedEventId={eventId}
              patientName={therapyReportData?.patientName}
              defaultLanguage={propBookedEventInfo?.patientLanguage ?? 'en'}
            />
            {renderSection('documents-upload')}
            {renderSection('next-visit')}
            {renderSection('private-notes')}
          </div>
          {reportPreview({ hiddenInUI: true })}
        </PrescriptionTabFinalParent>
      )
    }

    return (
      <PrescriptionTabFinalParent style={{ width: '100%' }}>
        <SEOComponent
          title={`${getTherapyReportSlug(therapyDiscipline)}_${therapyReportData?.patientNumber}_${dayjs
            .utc(therapyDate)
            .local()
            .format('YYYYMMDD_HHmmss')}`}
        />

        {/* Settings Component */}
        <TherapyReportSettings
          onSettingsChange={setSectionSettings}
          onOpen={(handler) => {
            settingsOpenRef.current = handler
          }}
        />
          
          <div 
            className="hide-on-print-only" 
            style={{ 
              margin: '0 auto', 
              width: '95%',
              backgroundColor: 'rgba(250, 245, 255, 0.3)', // Light purple page background
              minHeight: '100vh',
              padding: '24px',
            }}
          >
            {/* Save buttons moved to header - see SessionTopTabs */}
            {/* Render sections dynamically based on order from settings */}
            {(() => {
              const sectionsInOrder = getSectionsInOrder()
              const renderedSections = []
              let therapyHistoryRendered = false

              sectionsInOrder.forEach((sectionId) => {
                // Handle therapy-history and consultation-history together
                if (sectionId === 'therapy-history' || sectionId === 'consultation-history') {
                  if (!therapyHistoryRendered) {
                    renderedSections.push(
                      <div key="therapy-consultation-history" style={{ fontSize: '16px', width: '100%', display: 'block' }}>
                        <PMPSectionHeading></PMPSectionHeading>
                        <div style={{ width: '100%', display: 'block' }}>
                          <PatientVisitSummaryHandler
                            patientVisitSummary={auxiliaryDataFromProps?.patientVisitSummary}
                            role="therapist"
                            showTherapyHistory={isSectionVisible('therapy-history')}
                            showConsultationHistory={isSectionVisible('consultation-history')}
                          />
                        </div>
                      </div>
                    )
                    therapyHistoryRendered = true
                  }
                  return // Skip rendering consultation-history separately
                }

                // Render other sections
                const sectionContent = renderSection(sectionId)
                if (sectionContent) {
                  renderedSections.push(sectionContent)
                }
              })

              return renderedSections
            })()}
          </div>
          {reportPreview({ hiddenInUI: true })}
          
          {/* No Next Visit Dialog */}
          <Dialog
            open={noNextVisitDialog.isOpen}
            onClose={() => {
              console.log('🔍 No next visit dialog closed by user')
              noNextVisitDialog.reject()
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            <TherapyReportConfirmation
              submitForm={() => {
                console.log('🔍 No next visit dialog submit clicked, reason:', nextVisitOmissionReason)
                if (nextVisitOmissionReason?.trim()) {
                  console.log('✅ Resolving no-next-visit dialog with reason')
                  noNextVisitDialog.resolve({ reason: nextVisitOmissionReason })
                } else {
                  console.log('⚠️ No reason provided, cannot submit')
                  toast.error('Please provide a reason for not setting a next visit date')
                }
              }}
              isSaving={false}
              closeModal={() => {
                console.log('🔍 No next visit dialog cancelled')
                noNextVisitDialog.reject()
              }}
              reportReason={nextVisitOmissionReason}
              setReportReason={(value) => {
                latestNextVisitOmissionReasonRef.current = value
                setNextVisitOmissionReason(value)
              }}
            />
          </Dialog>
        </PrescriptionTabFinalParent>
      )
  },
)

export default TherapyReportMakingPage
